<template>
  <div>
    <h3>{{ pageHeading }}</h3>

    <div v-if="isTableLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>

    <template v-else>
      <div class="custom-search d-flex justify-content-between align-items-center mb-1">
        <SalesSearchFilters
          :filter-date-from="filterDateFrom"
          :filter-date-to="filterDateTo"
          :filter-category-id="filterCategoryId"
          :filter-total-from="filterTotalFrom"
          :filter-total-to="filterTotalTo"
          :filter-supervisor-id="filterSupervisorId"
          :search-term="searchTerm"
          :category-filter-options="categoryFilterOptions"
          :supervisor-filter-options="supervisorFilterOptions"
          :can-see-all-supervisors-orders="canSeeAllSupervisorsOrders"
          :company-purpose="companyPurpose"
          :supervisor-picture-fallback="supervisorPictureFallback"
          :has-active-filters="hasActiveOrderFilters"
          :filtered-count="salesWithPaymentsTotal"
          @update:filter-date-from="filterDateFrom = $event"
          @update:filter-date-to="filterDateTo = $event"
          @update:filter-category-id="filterCategoryId = $event"
          @update:filter-total-from="filterTotalFrom = $event"
          @update:filter-total-to="filterTotalTo = $event"
          @update:filter-supervisor-id="onFilterSupervisorChange"
          @update:search-term="searchTerm = $event"
          @reset="resetOrderFilters"
        />
        <b-form-group
          v-if="canCreateOrders"
          class="mb-0"
        >
          <b-button
            variant="primary"
            class="mx-1"
            size="sm"
            :to="{ name: companyPurpose !== 'rent' ? 'new-order' : 'new-booking' }"
          >
            <span>Create {{ companyPurpose !== 'rent' ? 'order' : 'booking' }}</span>
          </b-button>
        </b-form-group>
      </div>

      <vue-good-table
        ref="salesTable"
        mode="remote"
        :columns="tableColumns"
        :rows="salesWithPayments"
        :total-rows="salesWithPaymentsTotal"
        :search-options="tableSearchOptions"
        :pagination-options="tablePaginationOptions"
        :sort-options="tableSortOptions"
        @column-filter="onColumnFilter"
        @sort-change="onTableSort"
        @page-change="onSalesPageChange"
        @on-row-click="clickRow"
      >
        <template slot="table-column" slot-scope="props">
          <span
            v-if="props.column.label === 'Customer'"
            class="d-flex align-items-center justify-content-center"
          >
            <span class="mr-25">
              {{props.column.label}}
            </span>
            <feather-icon
              icon="InfoIcon"
              size="16"
              v-b-tooltip.hover
              class="text-primary"
              title="Click on a row in this column to see customer details"
            />
          </span>
          <span
            v-else-if="props.column.label === 'Payment Status'"
            class="d-flex align-items-center justify-content-center"
          >
            <span class="mr-25">
              {{props.column.label}}
            </span>
            <feather-icon
              icon="InfoIcon"
              size="16"
              v-b-tooltip.hover
              class="text-primary"
              title="Click on a row in this column to see payment history"
            />
          </span>
          <span
            v-else-if="props.column.label === 'Type'"
            class="d-flex align-items-center justify-content-center"
          >
            <span class="mr-25">
              {{ props.column.label }}
            </span>
            <feather-icon
              icon="InfoIcon"
              size="16"
              v-b-tooltip.hover
              class="text-primary"
              title="Click Delivery to see shipping records"
            />
          </span>

          <span v-else>
              {{props.column.label}}
          </span>
        </template>
        <template #column-filter="{ column, updateFilters }">
          <input
            v-if="column.field === 'pickup' || (companyPurpose === 'rent' && column.field === 'date')"
            type="date"
            class="form-control form-control-sm"
            @change="(event) => updateFilters(column, event.target.value || null)"
          />
        </template>

        <template #table-row="props">

          <span v-if="props.column.field === 'customer_name'">
            <b-badge
              v-if="hasNumericUserId(props.row)"
              class="call-badge"
              :variant="'light-dark'"
              @click="fieldCustomerDetails(props.row)"
            >
              <DatatableUserText :value="props.row.customer_name" />
            </b-badge>
          </span>

          <span
            v-else-if="props.column.field === 'supervisor_name'"
            class="d-inline-flex align-items-center"
          >
            <template v-if="props.row.supervisor_name">
              <img
                :src="supervisorPictureUrl(props.row.supervisor)"
                alt=""
                class="order-supervisor-thumb order-supervisor-thumb--table mr-50"
              >
              <span><DatatableUserText :value="props.row.supervisor_name" /></span>
            </template>
          </span>
          

          <span v-else-if="props.column.field === 'total'">
            {{ $formatCurrency($resolveSaleTotal(props.row)) }}
          </span>

          <span v-else-if="props.column.field === 'unit'" class="d-flex align-items-center">
            <span class="mr-25">
              <span :class="{ 'deleted-product-name': props.row.unit_deleted }">
                <DatatableUserText
                  field="unit"
                  :value="props.row.unit"
                />
              </span>
              <template v-if="props.row.package_name !== null">
                - <DatatableUserText
                  field="package_name"
                  :value="props.row.package_name"
                />
              </template>
              <div
                v-for="item in orderLineItemsPreview(props.row).slice(0, 4)"
                :key="`${item.line_type}-${item.id}`"
                class="pb-0"
                :class="orderLineItemsFontClass(props.row)"
              >
                <span :class="{ 'deleted-product-name': item.deleted }"><DatatableUserText field="name" :value="item.name" /></span>
                <span class="order-line-qty"> ×{{ item.items_quantity }}</span>
              </div>
              <div v-if="orderLineItemsPreview(props.row).length > 4">
                <b-button
                  variant="outline-secondary"
                  size="sm"
                  class="order-lines-view-all mt-25"
                  @click="openEditModal(props.row)"
                  v-b-modal.edit-modal
                >
                  View all ({{ orderLineItemsPreview(props.row).length }})
                </b-button>
              </div>
            </span>
          </span>
          <span v-else-if="props.column.field === 'created_at'">
            <strong>#{{ props.row.id }}</strong> {{ formatInstant(props.row.created_at) }}
            <small v-if="isAdmin && props.row.internal_id" class="text-muted d-block">DB #{{ props.row.internal_id }}</small>
            <div
              v-if="saleRepeatType(props.row)"
              class="very-small mt-25"
            >
              <feather-icon
                icon="RepeatIcon"
                size="12"
                class="text-body align-middle mr-25"
              />
              repeats {{ saleRepeatType(props.row) }}
            </div>
          </span>
          <span v-else-if="props.column.field === 'date'">
            <strong>#{{ props.row.id }}</strong> {{ formatSaleCivilDate(props.row.date) }}
            <small v-if="isAdmin && props.row.internal_id" class="text-muted d-block">DB #{{ props.row.internal_id }}</small>
            <div>
              <feather-icon
                  icon="ClockIcon"
                  size="12"
                  class="text-body align-middle mr-25"
              />
              <small v-if="!props.row.time_interval">Any time</small>
              <DatatableUserText
                v-else
                tag="small"
                :value="props.row.time_interval"
              />
            </div>
          </span>

          <b-badge v-else-if="props.column.field === 'status'"
              :variant="shouldShowReturnStatusLabel(props.row.status, props.row)
                ? 'secondary'
                : getStatusDisplay(props.row.status, props.row).class"
              :class="[
                'call-badge',
                { 'badge-returned-status': shouldShowReturnStatusLabel(props.row.status, props.row) },
              ]"
              v-b-modal.edit-modal @click="openEditModal(props.row, canChangeThisSale(props.row) ? 'status' : 'all')"
          >
            <span
              class="d-inline-flex align-items-center"
              :class="{
                'shipped-status-label': shouldShowShippingStatusLabel(props.row.status, props.row)
                  || shouldShowReturnStatusLabel(props.row.status, props.row),
              }"
            >
              <feather-icon
                v-if="shouldShowReturnStatusLabel(props.row.status, props.row)"
                icon="RotateCcwIcon"
                size="18"
                class="shipped-status-icon align-middle"
              />
              <feather-icon
                v-else-if="shouldShowShippingStatusLabel(props.row.status, props.row)"
                icon="TruckIcon"
                size="18"
                class="shipped-status-icon align-middle"
              />
              <span>
                {{ getStatusDisplay(props.row.status, props.row).name }}
                {{ props.row.platform_status !== null ? ' / ' + props.row.platform_status : '' }}
              </span>
            </span>
          </b-badge>

          <b-badge v-else-if="props.column.field === 'payment_status'"

             @click="fieldPaymentHistory(props.row)"
             class="call-badge capitalize"
             :variant="paymentStatuses[props.row.payment_status] != undefined ? paymentStatuses[props.row.payment_status].class : 'light-dark'"
          >
            {{ paymentStatuses[props.row.payment_status] != undefined ? paymentStatuses[props.row.payment_status].name : 'Undefined' }}
          </b-badge>

          <span v-else-if="props.column.field === 'pickup'">
            <div class="d-flex flex-column align-items-center">
              <template v-if="isServicesOnlyOrder(props.row)">
                <span>Service</span>
              </template>
              <template v-else>
                <span v-if="props.row.pickup">Pick up</span>
                <b-badge
                  v-else
                  class="call-badge delivery-shipping"
                  variant="light-primary"
                  @click="openShippingsList(props.row)"
                >
                  Delivery
                </b-badge>
              </template>
              <small
                v-if="props.row.date"
                class="mt-25"
              >
                by {{ formatSaleCivilDate(props.row.date) }}
              </small>
              <span
                v-if="props.row.time_interval"
                class="very-small"
              >
                <feather-icon
                  icon="ClockIcon"
                  size="12"
                  class="text-body align-middle mr-25"
                />
                <DatatableUserText
                  tag="span"
                  :value="props.row.time_interval"
                />
              </span>
            </div>
          </span>


          <!-- Column: Survey -->
          <!--
          <span v-else-if="props.column.field === 'survey'">
            <feather-icon v-if="!props.row.survey" icon="XIcon" size="24" />
            <b-button
              v-else
              variant="primary"
              :to="`/charter-survey/${props.row.id}`"
              size="sm"
            >
              Submitted Answers
            </b-button>
          </span>
          -->

          <!-- Column: Action -->
          <span v-else-if="props.column.field === 'platform'" class="text-center">
            <div v-if="props.row.platform ?? false" class="text-center">
              <img v-if="props.row.platform.picture ?? false"
                   :src="props.row.platform.picture"
                   :style="{ width: '50px' }"
              />
              <div>
                <small>
                  <a target="_blank"
                     :href="props.row.platform.url + (props.row.platform.type == 'WooCommerce' ? 'wp-admin/admin.php?page=wc-orders&action=edit&id=' + props.row.id_on_platform : '')"
                  >{{ props.row.platform.name }}<br/><span class="very-small">{{ props.row.platform.type }}</span></a>
                </small>
              </div>
            </div>
          </span>

          <span class="action-link-group" v-else-if="props.column.field === 'action'">
            <b-dropdown
              variant="link"
              toggle-class="text-decoration-none"
              no-caret
              right
            >
              <template v-slot:button-content>
                <feather-icon
                  icon="MoreVerticalIcon"
                  size="16"
                  class="text-body align-middle"
                />
              </template>
              <b-dropdown-item
                v-if="props.row.status !== 6"
                v-b-modal.edit-modal
                @click="openEditModal(props.row)"
              >
                <feather-icon
                  :icon="canEditSaleFromActions(props.row) ? 'Edit2Icon' : 'EyeIcon'"
                  class="mr-50"
                />
                <span>{{ canEditSaleFromActions(props.row) ? 'Edit / View' : 'View' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="companyPurpose !== 'rent' && !props.row.pickup && !isServicesOnlyOrder(props.row)"
                @click="(canChangeThisSale(props.row) && props.row.has_remaining_to_ship) ? openCreateShipping(props.row) : openShippingsList(props.row)"
              >
                <feather-icon icon="TruckIcon" class="mr-50" />
                <span>{{ (canChangeThisSale(props.row) && props.row.has_remaining_to_ship) ? 'Create Shipping' : 'View Shipping' }}</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="companyPurpose !== 'rent' && isProductOrder(props.row) && !isServicesOnlyOrder(props.row)"
                @click="openReturnsModal(props.row)"
              >
                <feather-icon icon="RotateCcwIcon" class="mr-50" />
                <span>{{ (!canChangeThisSale(props.row) || props.row.has_returns) ? 'Returns' : 'Create a return' }}</span>
              </b-dropdown-item>
              <!--
              <b-dropdown-item
                  v-if="props.row.status !== 5 && props.row.status !== 6"
                  v-b-modal.add-payment-modal
                  @click="openAddPaymentModal(props.row)"
              >
                <feather-icon icon="DollarSignIcon" class="mr-50" />
                <span>Add Payment</span>
              </b-dropdown-item>
              -->
              <b-dropdown-item
                v-if="canChangeThisSale(props.row) && hasInvoiceCustomer(props.row)"
                @click="sendInvoice(props.row.id)"
              >
                <feather-icon icon="MailIcon" class="mr-50" />
                <span>Send invoice to customer</span>
              </b-dropdown-item>
              <b-dropdown-item
                v-if="canCreateOrders && props.row.platform === null"
                @click="repeatThisSale(props.row.id)"
              >
                <feather-icon icon="RepeatIcon" class="mr-50" />
                <span>Repeat this {{ companyPurpose !== 'rent' ? 'order' : 'booking' }}</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="hasInvoiceCustomer(props.row)"
                @click="openPrintModal(props.row, 'invoice')"
              >
                <feather-icon icon="FileTextIcon" class="mr-50" />
                <span>Print invoice</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="hasNumericUserId(props.row) && false"
                @click="openPrintModal(props.row, 'label')"
              >
                <feather-icon icon="PrinterIcon" class="mr-50" />
                <span>Print the label</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-if="hasNumericUserId(props.row) && !isServicesOnlyOrder(props.row)"
                @click="openPrintModal(props.row, 'packing_slip')"
              >
                <feather-icon icon="PackageIcon" class="mr-50" />
                <span>Print packing slip</span>
              </b-dropdown-item>

              <b-dropdown-item
                v-show="hasNumericUserId(props.row) && !isServicesOnlyOrder(props.row)"
                @click="openPrintModal(props.row, 'delivery_note')"
              >
                <feather-icon icon="PrinterIcon" class="mr-50" />
                <span>Print delivery note</span>
              </b-dropdown-item>

              <!-- <b-dropdown-item @click="openPrintModal(props.row, 'message')">
                <feather-icon icon="PrinterIcon" class="mr-50" />
                <span>Print the message</span>
              </b-dropdown-item> -->

              <b-dropdown-item v-if="companyPurpose === 'rent'"
                    @click="resendSaleEmail(props.row.id)"
                >
                <feather-icon icon="MailIcon" class="mr-50" />
                <span>Resend email</span>
              </b-dropdown-item>

              <b-dropdown-item @click="deleteItem(props.row.id)">
                <feather-icon icon="TrashIcon" class="mr-50" />
                <span>Delete</span>
              </b-dropdown-item>
            </b-dropdown>
          </span>


          <DatatableCellValue
            v-else
            :field="props.column.field"
            :value="props.formattedRow[props.column.field]"
          />
        </template>

        <!-- pagination -->
        <template #pagination-bottom="props">
          <TablePagination
              :value="salesTablePage"
              :total-rows="props.total"
              :per-page="pageLength"
              @input="(value) => props.pageChanged({ currentPage: value })"
              @per-page-change="(n) => { pageLength = n; salesTablePage = 1; props.perPageChanged({ currentPerPage: n }) }"
            />
        </template>
      </vue-good-table>
    </template>

    <b-modal
        id="edit-modal"
        size="lg"
        :title="editModalTitle"
    >
      <!-- Add this loading overlay -->
      <div v-if="isEditLoading" class="modal-overlay d-flex align-items-center justify-content-center">
        <b-spinner variant="primary" label="Loading..."></b-spinner>
      </div>
      <b-form>
        <div
          v-if="showOrderSupervisorBlock"
          class="d-flex align-items-center justify-content-between mb-1 order-supervisor-header"
        >
          <div class="d-flex align-items-center">
            <img
              :src="editedSupervisorPicture"
              alt=""
              class="order-supervisor-thumb mr-50"
            >
            <div>
              <div class="font-weight-bold">Supervisor</div>
              <div>{{ edited.supervisor_name || 'Unassigned' }}</div>
            </div>
            <b-button
              v-if="canChangeOrderSupervisor"
              variant="outline-secondary"
              size="sm"
              class="ml-1"
              @click="toggleSupervisorChange"
            >
              Change
            </b-button>
          </div>
          <div
            v-if="canChangeOrderSupervisor && showSupervisorChange"
            class="order-supervisor-change ml-1"
          >
            <ImageTextSelect
              :model-value="edited.supervisor_id"
              :options="supervisorChangeOptions"
              :fallback-image="supervisorPictureFallback"
              placeholder="Select supervisor"
              @update:model-value="onSupervisorChangeSelect"
            />
          </div>
        </div>
        <b-row>
          <b-col cols="6">
            <template >
              <b-row v-if="edited.platform ?? false" class="pb-1 w-100">
                <b-col cols="4">
                  <img v-if="edited.platform.picture ?? false"
                       :src="edited.platform.picture"
                       :style="{ maxWidth: '100px' }"
                  />
                </b-col>
                <b-col cols="8" class="pt-1">
                  <a target="_blank" :href="edited.platform.url">{{ edited.platform.name }}</a>
                  <br>
                  <a target="_blank"
                     :href="edited.platform.url + (edited.platform.type == 'WooCommerce' ? 'wp-admin/admin.php?page=wc-orders&action=edit&id=' + edited.id_on_platform : '')"
                  ><b-button variant="outline-secondary" class="my-1 not-very-small">Check on {{edited.platform.type}} #{{ edited.id_on_platform }}</b-button></a>
                </b-col>
              </b-row>
            </template>
          </b-col>
          <b-col cols="6">
            <b-form-checkbox v-model="edited.cancelled" switch class="mb-1 w-100 text-right" v-show="false" > <!--v-show="editAction === 'all'"-->
              <!--
              <span v-if="edited.cancelled">Cancelled</span>
              <span v-else>Cancel this {{ companyPurpose !== 'rent' ? 'order' : 'booking' }}</span>
              -->
              <span >Cancel this {{ companyPurpose !== 'rent' ? 'order' : 'booking' }}</span>
            </b-form-checkbox>
          </b-col>
        </b-row>
        <template>

          <b-row v-if="companyPurpose !== 'rent'" class="py-2" v-show="editAction === 'all'">
            <b-col cols="12" >
              <Products
                  :products="edited.products"
                  :sale="edited"
                  :products-read-only="isOrderFieldsReadOnly"
                  @update:products="updateProducts"
              />
            </b-col>
            <b-col cols="12" class="mt-2">
              <Services
                  :services="edited.services"
                  :sale="edited"
                  :services-read-only="isOrderFieldsReadOnly"
                  @update:services="updateServices"
              />
            </b-col>
          </b-row>

          <b-row v-if="!edited.pickup && !isEditedServicesOnly" v-show="editAction === 'all'">
            <b-col cols="12">
              <h7>Delivery address</h7>
              <b-container class="border my-1 p-1">
                <b-row class="w-100">
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Country"
                        label-for="customer_address_country"
                        class="mb-2"
                    >
                      <CountrySelect
                          input-id="customer_address_country"
                          v-model="edited.address.country_id"
                          :clearable="false"
                          :disabled="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.country_id.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Region"
                        label-for="customer_address_region"
                        class="mb-2"
                    >
                      <RegionSelect
                          input-id="customer_address_region"
                          v-model="edited.address.region_id"
                          :country-id="edited.address.country_id || companyCountryIdFallback"
                          :clearable="false"
                          :disabled="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.region_id.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="City"
                        label-for="customer_address_city"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_city"
                          v-model="edited.address.city"
                          :readonly="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.city.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Postal code"
                        label-for="customer_address_zip_code"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_zip_code"
                          v-model="edited.address.zip_code"
                          :readonly="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.zip_code.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Address line 1"
                        label-for="customer_address_line_1"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_line_1"
                          v-model="edited.address.address_line_1"
                          :readonly="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.address_line_1.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Address line 2"
                        label-for="customer_address_line_2"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_line_2"
                          v-model="edited.address.address_line_2"
                          :readonly="isFieldReadOnly"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Name"
                        label-for="customer_address_name"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_name"
                          v-model="edited.address.name"
                          :readonly="isFieldReadOnly"
                      />
                      <small class="text-danger" v-if="vSale.edited.address.name.$error">Required</small>
                    </b-form-group>
                  </b-col>
                  <b-col cols="12" lg="6">
                    <b-form-group
                        label="Phone"
                        label-for="customer_address_phone"
                        class="mb-2"
                    >
                      <b-form-input
                          id="customer_address_phone"
                          v-model="edited.address.phone"
                          :readonly="isFieldReadOnly"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="12">
                    <b-form-group
                        label="Special notes"
                        label-for="description"
                    >
                      <b-form-textarea
                          v-model="edited.address.description"
                          placeholder="Special notes"
                          rows="3"
                          max-rows="6"
                          :readonly="isFieldReadOnly"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-container>
            </b-col>
          </b-row>

          <b-row>
            <b-col cols="12" lg="6">
              <b-form-group label="Status" label-for="status">
                <div
                  v-if="showStaffNewStatusHint"
                  class="mb-50 text-muted"
                >
                  Current status: <strong>New</strong>
                </div>
                <v-select
                    id="status"
                    v-model="edited.status"
                    label="name"
                    :options="statusOptionsForEdit"
                    :reduce="status => status.id"
                    :clearable="false"
                    :disabled="!isStatusFieldEditable"
                    :placeholder="showStaffNewStatusHint ? 'Select new status' : ''"
                />
                <div
                  v-if="editStatusContextLabels.length"
                  class="d-flex flex-wrap align-items-center mt-50 order-status-context-labels"
                >
                  <span
                    v-for="label in editStatusContextLabels"
                    :key="label.key"
                    class="order-status-context-label d-inline-flex align-items-center"
                    :class="label.className"
                  >
                    <feather-icon
                      :icon="label.icon"
                      size="14"
                      class="mr-25"
                    />
                    {{ label.text }}
                  </span>
                </div>
                <small v-if="hasDeletedOrderProducts" class="text-danger d-block mt-25">
                  Status cannot be changed while this order includes deleted products.
                </small>
              </b-form-group>
            </b-col>
          </b-row>

          <b-row v-show="editAction === 'all'">
            <b-col cols="12" lg="6">
              <b-form-group label="Payment status" label-for="payment_status">
                <v-select
                  id="payment-status-select"
                  v-model="edited.payment_status"
                  label="name"
                  :options="Object.values(paymentStatuses)"
                  :clearable="false"
                  :disabled="true"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="6" class="d-flex align-items-end">
              <b-form-group class="w-100" label-for="manage-payments-btn">
                <template #label>
                  <span class="invisible">Manage</span>
                </template>
                <b-button
                  id="manage-payments-btn"
                  variant="outline-primary"
                  size="sm"
                  class="w-100"
                  @click="openManagePaymentsFromEdit"
                >
                  Manage payments
                </b-button>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group v-if="companyPurpose === 'rent'"
              :label="companyPurpose !== 'rent' ? 'Product' : 'Unit'"
              v-show="editAction === 'all'"
          >
              <v-select
                  :options="units"
                  :items="units"
                  v-model="edited.unitObject"
                  item-text="name"
                  item-value="id"
                  label="name"
                  :clearable="false"
                  :disabled="isFieldReadOnly"
                  required
                  @input="unitChanged"
              />
          </b-form-group>

          <b-form-group label="Package" label-for="text" v-if="companyPurpose === 'rent'" v-show="editAction === 'all'"> <!--:options="packages"-->
            <v-select
              id="package_name-select"
              v-model="edited.packageName"
              label="text"
              :clearable="false"
              :disabled="isFieldReadOnly"
              @input="loadBlockDates"
            />
          </b-form-group>

          <b-row
            v-show="editAction === 'all'"
            class="edit-order-date-row"
          >
            <b-col
              v-if="companyPurpose !== 'rent' && !isEditedServicesOnly"
              cols="12"
              lg="4"
            >
              <b-row>
                <b-col cols="6">
                  <b-form-group
                      :label="'In store pick up'"
                      label-for="pickup"
                  >
                    <b-form-checkbox v-model="edited.pickup" switch class="mb-1" :disabled="isFieldReadOnly || !!edited.platform">

                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
                <b-col cols="6">
                  <b-form-group
                      :label="'Delivery'"
                      label-for="delivery"
                  >
                    <b-form-checkbox v-model="edited.delivery" switch class="mb-1" :disabled="isFieldReadOnly || !!edited.platform">

                    </b-form-checkbox>
                  </b-form-group>
                </b-col>
              </b-row>
            </b-col>
            <b-col
              cols="12"
              md="6"
              :lg="(companyPurpose !== 'rent' && !isEditedServicesOnly) ? 4 : 6"
            >
              <b-form-group
                  :label="editOrderDateLabel"
              >
                <b-form-datepicker
                  id="start_at-datepicker"
                  v-model="edited.date"
                  :min="minDate"
                  :date-disabled-fn="dateDisabled"
                  :disabled="isDatepickerDisabled || isFieldReadOnly"
                  :placeholder="datepickerPlaceholder"
                  locale="en"
                  :date-format-options="{ year: 'numeric', month: 'short', day: 'numeric' }"
                  @input="dateChanged"
                  :readonly="isFieldReadOnly || !!edited.platform"
                />
              </b-form-group>
            </b-col>
            <b-col
              cols="12"
              md="6"
              :lg="(companyPurpose !== 'rent' && !isEditedServicesOnly) ? 4 : 6"
            >
              <b-form-group
                  :label="editOrderTimeIntervalLabel"
                  label-for="time-interval"
              >
                <b-form-input
                    id="time-interval"
                    v-model="edited.time_interval"
                    placeholder="any time"
                    maxlength="50"
                    :readonly="isFieldReadOnly || !!edited.platform"
                />
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group
              v-if="companyPurpose === 'rent'"
              label="Number of guests"
              label-for="details"
              v-show="editAction === 'all'"
          >
            <v-select
                    v-model="edited.details"
                    placeholder="Enter guests number"
                    :options="guestsOptions"
                    :clearable="false"
                    :disabled="isFieldReadOnly"
            />
          </b-form-group>

          <template
            v-if="companyPurpose === 'rent'"
            v-show="editAction === 'all'"
          >
            <b-form-group label="Choose the time">
              <v-select
                id="select-time"
                v-model="edited.time"
                placeholder="Select time"
                label="text"
                :options="times"
                :clearable="false"
                :selectable="timesDisabled"
                :disabled="isDatepickerDisabled || isFieldReadOnly"
              />
            </b-form-group>
          </template>

          <b-row v-show="editAction === 'all'">
            <b-col cols="12" lg="2">
              <b-form-group :label="(companyPurpose !== 'rent' ? 'Subtotal' : 'Price per hour') + ' '" label-for="subtotal">
                <b-form-input
                    type="number" v-model="edited.subtotal"
                    :placeholder="companyPurpose !== 'rent' ? 'Subtotal' : 'Price per hour'"
                    readonly
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="2">
              <b-form-group :label="(companyPurpose !== 'rent' ? 'Shipping' : 'Delivery') + ' '" label-for="shipping" v-show="showShipping">
                <b-form-input
                    type="number"
                    v-model="edited.shipping"
                    min="0"
                    :readonly="isFieldReadOnly || !!edited.platform"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="2">
              <b-form-group :label="'Discount (in %)'" label-for="discount">
                <b-form-input
                    type="number"
                    v-model="edited.discount"
                    :placeholder="'Discount in %'"
                    max="99"
                    min="0"
                    :readonly="isFieldReadOnly || !!edited.platform"
                />
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="3">
              <b-form-group
                v-show="Number(editedTaxPercent || 0) !== 0"
                :label="'Tax (' + saleTax.name + ')'"
              >
                <span>{{ editedTaxPercent }}%</span>
              </b-form-group>
            </b-col>
            <b-col cols="12" lg="3">
              <b-form-group :label="'Total'">
                <span>{{ $formatCurrency(calcTotal()) }}</span>
              </b-form-group>
            </b-col>
          </b-row>

          <b-form-group
              label="Special notes / Description"
              label-for="description"
              v-show="editAction === 'all'"
          >
            <b-form-textarea
                v-model="edited.description"
                placeholder="Description"
                rows="3"
                max-rows="6"
                :readonly="isFieldReadOnly"
            />
          </b-form-group>

          <b-form-group
              label="Customer note"
              label-for="customer_note"
              v-show="editAction === 'all'"
          >
            <b-form-textarea
                v-model="edited.customer_note"
                placeholder="Customer note"
                rows="3"
                max-rows="6"
                :readonly="isFieldReadOnly"
            />
          </b-form-group>

          <b-form-group :label="`Repeat this ${(companyPurpose !== 'rent' ? 'order' : 'booking')}`" label-for="repeat" v-show="editAction === 'all'">
            <v-select
                id="repeat-select"
                v-model="edited.repeat"
                :options="Object.values(repeatSelect)"
                :clearable="true"
                :disabled="isFieldReadOnly"
            />
          </b-form-group>
          <b-form-group label-for="details" v-if="edited.repeat !== null" :label="`Repeat details (${repeatSamples[edited.repeat]})`" v-show="editAction === 'all'">
            <b-form-input
                v-model="edited.repeat_details"
                id="repeat-details"
                :readonly="isFieldReadOnly"
            />
          </b-form-group>

        </template>
      </b-form>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          class="float-right"
          @click="$bvModal.hide('edit-modal')"
        >
          Close
        </b-button>
        <b-button
          v-if="canSaveOrderEdit"
          variant="primary"
          class="float-right"
          @click="editSubmit"
          :disabled="isEditLoading"
        >
          <b-spinner v-if="isEditLoading" label="Spinning" small />
          <span v-else> Save </span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="add-payment-modal"
      :title='"Add payment for #" + payment.saleId'
    >
      <b-form>
        <b-form-group label="Payment Type" label-for="payment-type">
          <v-select
              id="payment-type"
              class="mb-1"
              v-model="payment.type"
              placeholder="Payment Type"
              label="text"
              :options="paymentTypes"
              :clearable="false"
          />
        </b-form-group>
        <b-form-group label="Payment Method" label-for="payment-method">
          <v-select
              id="payment-method"
              class="mb-1"
              v-model="payment.method"
              placeholder="Payment Method"
              label="text"
              :options="paymentMethods"
              :clearable="false"
          />
        </b-form-group>
        <b-form-group label="Payment Amount">
          <b-form-input
              type="number"
              v-model="payment.amount"
              :readonly="lockPaymentAmountToDue"
              :disabled="lockPaymentAmountToDue"
              :min="lockPaymentAmountToDue ? paymentAmountDue : undefined"
          />
        </b-form-group>
      </b-form>
      <template #modal-footer>
        <b-button
            variant="outline-secondary"
            class="float-right"
            @click="$bvModal.hide('add-payment-modal')"
        >
          Close
        </b-button>
        <b-button
            variant="primary"
            class="float-right"
            @click="addPaymentSubmit"
            :disabled="isAddPaymentLoading || !canSubmitAddPayment"
        >
          <b-spinner v-if="isAddPaymentLoading" label="Spinning" small />
          <span v-else>Add Payment</span>
        </b-button>
      </template>
    </b-modal>

    <b-modal
            id="payment-history-modal"
            ok-only
            :title="'Payment History #' + this.edited.id"
            size="xl"
    >
      <b-row class="payment-history-layout">
        <b-col
          cols="12"
          lg="9"
          class="order-2 order-lg-1"
        >
          <div
              v-if="isPaymentHistoryLoading"
              class="text-center"
          >
            <b-spinner label="Loading..." />
          </div>
          <div v-else-if="paymentsHistory.length">

            <div>
              <span v-if="paymentStatus != 3" :class="['ul_payments']">
                <b-badge :variant="'light-primary'" class="mr-1 p-1 my-1">
                  <span>{{ 'Total' }}</span> <strong>{{ $formatCurrency(totalPrice) }}</strong>
                </b-badge>
                <b-badge :variant="'light-success'" class="mr-1 p-1 my-1">
                  <span>{{ 'Paid' }}</span> <strong>{{ $formatCurrency(alreadyPaid) }}</strong>
                </b-badge>
                <b-badge :variant="'light-warning'" class="mr-1 p-1 my-1">
                  <span>{{ 'Refunded' }}</span> <strong>{{ $formatCurrency(refundedAmount) }}</strong>
                </b-badge>
                <span v-if="(/*remainingToPay > 0*/ true)">
                  <b-badge :variant="'light-danger'" class="mr-1 p-1 my-1">
                    <span>{{ remainingToPay >= 0 ? 'Amount due' : 'Overpaid' }}</span> <strong>{{ $formatCurrency(remainingToPay < 0 ? remainingToPay * (-1) : remainingToPay) }}</strong>
                  </b-badge>
                </span>
              </span>
              <span v-else :class="['ul_payments']">
                <b-badge :variant="'light-success'" class="mr-1 p-1 my-1">
                  <span>{{ 'Fully Paid' }}</span> <strong>{{ $formatCurrency(alreadyPaid, null) }}</strong>
                </b-badge>
              </span>
            </div>
            <div>
              <vue-good-table
                  id='paymentsHistoryTable'
                  ref='paymentsHistoryTable'
                  class="payments-history-table"
                  :class="{ 'payments-history-table--compact': isPaymentHistoryCompact }"

                  :columns="paymentColumnsDisplay"
                  :rows="paymentsHistory"
                  :pagination-options="{
                    enabled: false,
                    perPage: pageLength,
                  }"

                  @column-filter="onColumnFilter"
                  @row-click="clickRowPayments"
                  @selected-rows-change="paymentRowsSelected"
                  :select-options="paymentTableSelectOptions"

                  selectable
              >
                <template #table-row="props">
                  <span v-if="props.column.field === 'mobile_view'">
                    <b-button
                      variant="link"
                      class="p-0 payment-view-btn"
                      :aria-label="'View payment #' + props.row.id"
                      @click.stop="openPaymentRefundDetail(props.row)"
                    >
                      <feather-icon
                        icon="EyeIcon"
                        size="18"
                      />
                    </b-button>
                  </span>
                  <span v-else-if="props.column.field == 'amount'" @click="">
                    <b-form-input
                        type="number"
                        :min="props.row.amount > 0 ? 0.01 : 0"
                        :max="props.row.amount"
                        step="0.01"
                        size="sm"
                        required
                        class="refund_amount_field"
                        :value="refundAmountShowHide[props.formattedRow['id']]?.amount"
                        @input="clampRefundAmountInput(props.formattedRow['id'], props.row.amount, $event)"
                        @change="clampRefundAmountInput(props.formattedRow['id'], props.row.amount, $event)"
                        @blur="clampRefundAmountInput(props.formattedRow['id'], props.row.amount, $event)"
                        @paste="onRefundAmountPaste(props.formattedRow['id'], props.row.amount, $event)"
                        v-if="!isPaymentHistoryCompact && refundAmountShowHide[props.formattedRow['id']] !== undefined && refundAmountShowHide[props.formattedRow['id']]['visible'] =='show'"
                    />
                    <span v-else>{{ props.formattedRow[props.column.field] }}</span>
                  </span>
                  <span v-else-if="props.column.field == 'refunds'">
                    <div v-for="(refundData, index) in props.formattedRow['refunds']" :key="index">
                      {{ $formatCurrency(refundData['amount'], null) }} at {{ formatInstant(refundData['date']) }}
                    </div>
                  </span>
                  <span v-else-if="props.column.field == 'status'">
                    {{ this.paymentTypes.find(pt => pt.value === props.formattedRow['status']).text }}
                  </span>
                  <span v-else>
                      {{props.formattedRow[props.column.field]}}
                  </span>
                </template>
              </vue-good-table>
            </div>
            <div v-if="bookingLogs !=null && bookingLogs.length > 0" class="pt-2">
              <h4>Booking change history</h4>
              <hr />
              <ul :class="['ul_payments']">
                <li v-for="bookingLog in bookingLogs">
                  <div><strong>{{bookingLog.date}}</strong></div>

                  <div v-if="bookingLog.old_price != undefined && bookingLog.old_price != bookingLog.new_price">Price changed from <i>{{ $formatCurrency(bookingLog.old_price, null) }}</i> to <i>{{ $formatCurrency(bookingLog.new_price, null) }}</i></div>
                  <div v-if="bookingLog.old_guests  && bookingLog.old_guests != bookingLog.new_guests">Guests changed from <i>{{bookingLog.old_guests}}</i> to <i>{{bookingLog.new_guests}}</i></div>
                  <div v-if="bookingLog.old_unit != undefined && bookingLog.old_unit != bookingLog.new_unit">Unit changed from <i>{{bookingLog.old_unit}}</i> to <i>{{bookingLog.new_unit}}</i></div>
                  <div v-if="bookingLog.old_package != undefined && bookingLog.old_package != bookingLog.new_package">Package changed from <i>{{bookingLog.old_package}}</i> to <i>{{bookingLog.new_package}}</i></div>
                  <div v-if="bookingLog.old_date_time != undefined && bookingLog.old_date_time != bookingLog.new_date_time">Date changed from <i>{{bookingLog.old_date_time}}</i> to <i>{{bookingLog.new_date_time}}</i></div>
                </li>
              </ul>
            </div>
          </div>
          <div v-else>
            <div>
              <b-badge :variant="'light-primary'" class="mr-1 p-1 my-1">
                <span>{{ 'Total' }}</span> <strong>{{ $formatCurrency(totalPrice, null) }}</strong>
              </b-badge>
            </div>
            <div>
              <h5 class="text-center">
                There are no payments still
              </h5>
            </div>
          </div>
        </b-col>
        <b-col
          v-if="canChangeEditedOrder"
          cols="12"
          lg="3"
          class="order-1 order-lg-2 mb-2 mb-lg-0"
        >
          <b-alert
            v-if="isOrderCancelled || isOrderFullyPaid"
            show
            variant="secondary"
            class="p-1"
          >
            Payments cannot be added to cancelled or fully paid orders
          </b-alert>
          <b-form v-else>
            <b-form-group label="Payment Type" label-for="payment-type" v-show="false">
              <v-select
                  id="payment-type"
                  class="mb-1"
                  v-model="payment.type"
                  placeholder="Payment Type"
                  label="text"
                  :options="paymentTypes"
                  :clearable="false"
              />
            </b-form-group>
            <b-form-group label="Payment Method" label-for="payment-method">
              <v-select
                  id="payment-method"
                  class="mb-1"
                  v-model="payment.method"
                  placeholder="Payment Method"
                  label="text"
                  :options="paymentMethods"
                  :clearable="false"
              />
            </b-form-group>
            <b-form-group label="Payment Amount">
              <b-form-input
                  type="number"
                  v-model="payment.amount"
                  :readonly="lockPaymentAmountToDue"
                  :disabled="lockPaymentAmountToDue"
                  :min="lockPaymentAmountToDue ? paymentAmountDue : undefined"
              />
              <small v-if="lockPaymentAmountToDue" class="text-muted">
                Full amount due is required when partial payments are disabled.
              </small>
            </b-form-group>
            <b-form-group>
              <b-button
                  variant="primary"
                  class="float-right"
                  @click="addPaymentSubmit"
                  :disabled="isAddPaymentLoading || !canSubmitAddPayment"
              >
                <b-spinner v-if="isAddPaymentLoading" label="Spinning" small />
                <span v-else>Add Payment</span>
              </b-button>
            </b-form-group>
          </b-form>
        </b-col>
      </b-row>

      <template #modal-footer>
        <b-row class="w-100">
          <b-col cols="12" lg="9" class="p-0 mb-1 mb-lg-0">
            <b-button
                v-if="canChangeEditedOrder && paymentsHistory.length > 0 && !isPaymentHistoryCompact"
                variant="primary"
                @click="checkRefundPayments"
                :disabled="refundButtonDisabled"
            >
              Refund
            </b-button>
          </b-col>
          <b-col cols="12" lg="3" class="p-0">

            <b-button
                variant="outline-secondary"
                class="float-right"
                @click="$bvModal.hide('payment-history-modal')"
            >
              Close
            </b-button>

          </b-col>
        </b-row>

      </template>
    </b-modal>

    <b-modal
      id="payment-refund-detail-modal"
      :title="mobileRefundPayment ? `Payment #${mobileRefundPayment.id}` : 'Payment details'"
      @hidden="onPaymentRefundDetailHidden"
    >
      <div v-if="mobileRefundPayment" class="payment-refund-detail">
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Paid</div>
          <div class="payment-refund-detail__value">{{ $formatCurrency(mobileRefundPayment.paid, null) }}</div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Balance</div>
          <div class="payment-refund-detail__value">{{ $formatCurrency(mobileRefundPayment.amount, null) }}</div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Method</div>
          <div class="payment-refund-detail__value">{{ mobileRefundPayment.method }}</div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Type</div>
          <div class="payment-refund-detail__value">{{ mobileRefundPaymentTypeLabel }}</div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Refunded</div>
          <div class="payment-refund-detail__value">{{ $formatCurrency(mobileRefundPayment.amount_refunded, null) }}</div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Refunds list</div>
          <div class="payment-refund-detail__value">
            <div
              v-for="(refundData, index) in (mobileRefundPayment.refunds || [])"
              :key="index"
            >
              {{ $formatCurrency(refundData.amount, null) }} at {{ formatInstant(refundData.date) }}
            </div>
            <span v-if="!(mobileRefundPayment.refunds || []).length">—</span>
          </div>
        </div>
        <div class="payment-refund-detail__row">
          <div class="payment-refund-detail__label">Date</div>
          <div class="payment-refund-detail__value">{{ formatInstant(mobileRefundPayment.date) }}</div>
        </div>
        <b-form-group
          v-if="canChangeEditedOrder && !mobileRefundPayment.vgtDisabled"
          label="Refund amount"
          label-for="mobile-refund-amount"
          class="mt-1 mb-0"
        >
          <b-form-input
            id="mobile-refund-amount"
            type="number"
            :min="mobileRefundPayment.amount > 0 ? 0.01 : 0"
            :max="mobileRefundPayment.amount"
            step="0.01"
            required
            :value="refundAmountShowHide[mobileRefundPayment.id]?.amount"
            @input="clampRefundAmountInput(mobileRefundPayment.id, mobileRefundPayment.amount, $event)"
            @change="clampRefundAmountInput(mobileRefundPayment.id, mobileRefundPayment.amount, $event)"
            @blur="clampRefundAmountInput(mobileRefundPayment.id, mobileRefundPayment.amount, $event)"
            @paste="onRefundAmountPaste(mobileRefundPayment.id, mobileRefundPayment.amount, $event)"
          />
        </b-form-group>
        <b-alert
          v-else-if="mobileRefundPayment.vgtDisabled"
          show
          variant="warning"
          class="mt-1 mb-0 p-1"
        >
          This payment has no transaction id and must be refunded manually.
        </b-alert>
      </div>
      <template #modal-footer>
        <b-button
          variant="outline-secondary"
          @click="$bvModal.hide('payment-refund-detail-modal')"
        >
          Close
        </b-button>
        <b-button
          v-if="canChangeEditedOrder && mobileRefundPayment && !mobileRefundPayment.vgtDisabled"
          variant="primary"
          @click="checkMobileRefundPayment"
        >
          Refund
        </b-button>
      </template>
    </b-modal>

    <b-modal
      id="customer-modal"
      ok-only
      ok-title="Close"
      title="Customer details"
      cancel-variant="outline-secondary"
    >
      <b-form-group
        label="Name"
        class="capitalize"
      >
        <b-form-input
          v-model="customer.name"
          readonly
        />
      </b-form-group>
      <b-form-group
        label="Email"
        class="capitalize"
      >
        <b-form-input
          v-model="customer.email"
          readonly
        />
      </b-form-group>
      <b-form-group
        label="Phone"
        class="capitalize"
      >
        <b-form-input
          v-model="customer.phone"
          readonly
        />
      </b-form-group>
      <b-form-group
          label="Additional info"
          class="capitalize"
      >
        <b-form-textarea
            v-model="customer.description"
            rows="3"
            max-rows="6"
            readonly
        />
      </b-form-group>
      <!--
      <b-form-group
              label="Stripe Id"
              class="capitalize"
      >
        <b-form-input
                v-model="customer.stripe_id"
                readonly
        />
      </b-form-group>
      -->

    </b-modal>

    <b-modal
            id="refund-modal"
            ref="refund-modal"
            title="Confirm the refunding"
            ok-title="Refund"
            cancel-variant="outline-secondary"
            @ok="refundPayments"

    >
      <form
              ref="form"
              @submit.stop.prevent="handleSubmit"
      >
        <h5>Are you sure you want to refund selected payments?</h5>

        <div class="pt-2">
          <b-form-checkbox v-model="refundAlsoCancel" switch class="mb-1">
             Also Cancel this booking
          </b-form-checkbox>
        </div>
      </form>
    </b-modal>

    <SaleShippings
      ref="bookingShippings"
      :sale="shippingSale"
      :click-date="shippingsListClickDate"
      :allow-change="canChangeThisSale(shippingSale)"
      @create="openCreateShipping"
      @edit="openManageShippingEdit"
      @remaining-changed="onShippingRemainingChanged"
    />
    <ManageShipping
      :sale="shippingSale"
      :shipping-id="selectedShippingId"
      :click-date="manageShippingClickDate"
      @saved="onShippingSaved"
    />
    <SaleReturns
      :sale="returnsSale"
      :click-date="returnsClickDate"
      :allow-change="canChangeThisSale(returnsSale)"
      @saved="onReturnSaved"
    />
  </div>
  <Prints
      :clickDate="clickDate"
      :printType="printType"
      :saleId="saleToPrintId"
  />
</template>

<script>
import {
  BBadge,
  BPagination,
  BFormGroup,
  BFormInput,
  BDropdown,
  BDropdownItem,
  VBModal,
  BForm,
  BFormDatepicker,
  BSpinner,
  BButton,
  BFormCheckbox,
  BTable,
  VBTooltip, BCol, BRow, BFormTextarea, BFormSelect, BFormSelectOption,
} from "bootstrap-vue";
import 'vue-good-table-next/dist/vue-good-table-next.css'
import vSelect from "vue-select";
import { VueGoodTable } from "vue-good-table-next";
import { mapActions, mapGetters, mapState } from "vuex"
import TablePagination from '@/views/Components/Elements/TablePagination.vue';
import axios from "@/libs/axios";
import { toastSuccess, toastFailure } from "@/libs/toastification";
import {sweetWarning, sweetError} from "@/libs/sweet-alerts";
import {checkReservedDate, isTimeSelectable} from "@/libs/reserved-dates";
import {
  FULL_DAY_PACKAGE,
  THREE_FOURTHS_DAY_PACKAGE,
  HALF_DAY_PACKAGE,
  HARBOR_CRUISE,
  TEASER_ID
} from "@/constants/package-types.enum";
import {
  SALE_PAYMENT_STATUSES,
  SALE_STATUSES,
  SALE_STATUS_CANCELLED,
  getSaleStatusDisplay,
  shouldDisplayShippingStatusLabel,
  shouldDisplayReturnStatusLabel,
  shouldDisplaySaleStatusAsShipped,
  shouldDisplaySaleStatusAsPartlyShipped,
  shouldDisplaySaleStatusAsReturned,
  shouldDisplaySaleStatusAsPartlyReturned,
  isSaleEditLocked,
  SALE_REPEATS,
  SALE_REPEAT_SAMPLES,
  PAYMENT_METHODS,
  DEFAULT_PAYMENT_METHOD
} from "@/constants/enums";
import Products from "@/views/Components/Products.vue";
import Services from "@/views/Components/Services.vue";
import Prints from "@/views/Components/Prints.vue";
import ManageUnit from "@/views/Components/ManageUnit.vue";
import ManageShipping from "@/views/Components/ManageShipping.vue";
import SaleShippings from "@/views/Components/SaleShippings.vue";
import SaleReturns from "@/views/Components/SaleReturns.vue";
import ImageTextSelect from "@/views/Components/Elements/ImageTextSelect.vue";
import SalesSearchFilters from "@/views/Components/SalesSearchFilters.vue";
import CountrySelect from "@/views/Components/Elements/CountrySelect.vue";
import RegionSelect from "@/views/Components/Elements/RegionSelect.vue";
import { buildHierarchicalCategoryOptions } from '@/libs/categoryFilterOptions';
import {
  normalizeImageUrl,
  hasCompanyPrintAddress,
  companyPrintAddressMissingTitle,
  COMPANY_PRINT_ADDRESS_MISSING_BODY,
} from '@/libs/utils';
import { canStaffChange, canStaffCreate, canStaffViewOtherSupervisorsOrders, canStaffChangeOtherSupervisorsOrders } from '@/libs/staffPermissions';
import { formatCivilDate, formatCompanyDateTime, parseApiInstant } from '@/libs/companyTime'
import DatatableCellValue from '@/views/Components/Elements/DatatableCellValue.vue'
import DatatableUserText from '@/views/Components/Elements/DatatableUserText.vue'
import useVuelidate from "@vuelidate/core";
//import { validationMixin } from 'vuelidate';
import {minValue, numeric, required, requiredIf} from "@vuelidate/validators";

export default {
  components: {
    TablePagination,
    ManageUnit,
    ManageShipping,
    SaleShippings,
    SaleReturns,
    ImageTextSelect,
    SalesSearchFilters,
    CountrySelect,
    RegionSelect,
    BFormTextarea,
    Prints,
    BRow, BCol, Products, Services,
    VueGoodTable,
    BBadge,
    BPagination,
    BFormGroup,
    BFormInput,
    BDropdown,
    BDropdownItem,
    BForm,
    vSelect,
    BFormDatepicker,
    BSpinner,
    BButton,
    BFormCheckbox,
    BTable,
    BFormSelect,
    BFormSelectOption,
    DatatableCellValue,
    DatatableUserText,
  },
  directives: {
    "b-modal": VBModal,
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      editAction: 'all',
      clickDate: null,
      shippingSale: null,
      returnsSale: null,
      selectedShippingId: null,
      shippingsListClickDate: new Date(),
      returnsClickDate: new Date(),
      manageShippingClickDate: new Date(),
      saleToPrintId: null,
      printType: null,
      companyPurpose: window.companyPurpose,
      saleTax: window.saleTax,
      companyId: window.companyId,
      HALF_DAY_PACKAGE, HARBOR_CRUISE, TEASER_ID, FULL_DAY_PACKAGE, THREE_FOURTHS_DAY_PACKAGE,
      packages: null,
      isTableLoading: true,
      isEditLoading: false,
      showSupervisorChange: false,
      refundButtonDisabled: true,
      refundAlsoCancel:false,
      showShipping: true,
      searchTerm: "",
      filterDateFrom: null,
      filterDateTo: null,
      filterCategoryId: null,
      filterDeliveryDate: null,
      filterStatusId: '',
      filterPaymentStatusId: '',
      filterUnitKeyword: '',
      filterTotalFrom: '',
      filterTotalTo: '',
      filterSupervisorId: '',
      filterCustomerName: null,
      tableSort: {
        field: 'created_at',
        type: 'desc',
      },
      unitFilterOptions:[''],
      statuses: SALE_STATUSES,
      paymentStatuses: SALE_PAYMENT_STATUSES,
      repeatSelect: SALE_REPEATS,
      repeatSamples: SALE_REPEAT_SAMPLES,
      paymentMethods: PAYMENT_METHODS,
      paymentColumns:[
        {
          label: "#Id",
          field: "id",
          type: "string",
          thClass: "text-left",
          tdClass: "text-left",
          hidden: true
        },
        {
          label: "Paid",
          field: "paid",
          type: "numeric",
          thClass: "text-left",
          tdClass: "text-left",
          formatFn: this.cellMoneyFormat,
        },
        {
          label: "Balance",
          field: "amount",
          type: "numeric",
          thClass: "text-left",
          tdClass: "text-left",
          formatFn: this.cellMoneyFormat,
        },
        {
          label: "Method",
          field: "method",
          type: "string",
          thClass: "text-left",
          tdClass: "text-left",
        },
        {
          label: "Type",
          field: "status",
          type: "string",
          thClass: "text-center",
          tdClass: "text-center",
        },
        {
          label: "Refunded",
          field: "amount_refunded",
          type: "numeric",
          thClass: "text-left",
          tdClass: "text-left",
          formatFn: this.cellMoneyFormat,
        },
        {
          label: "Refunds list",
          field: "refunds",
          thClass: "text-left",
          tdClass: "text-left",
          html: 'true',
        },
        {
          label: "Date",
          field: "date",
          type: "string",
          thClass: "text-left",
          tdClass: "text-left",
          formatFn: this.formatInstant,
        }
      ],
      columns: [
        {
          label: 'Id & Date',
          field: companyPurpose !== 'rent' ? 'created_at' : 'date',
          width: '160px',
          type: companyPurpose !== 'rent' ? 'string' : 'date',
          dateInputFormat: 'yyyy-MM-dd',
          dateOutputFormat: 'yyyy-MM-dd',
          thClass: 'text-center',
          tdClass: 'text-left',
          ...(companyPurpose !== 'rent'
            ? {}
            : {
              filterOptions: {
                enabled: true,
                customFilter: true,
              },
            }),
        },
        {
          label: 'Total',
          field: 'total',
          type: 'number',
          thClass: 'text-center',
          tdClass: 'text-center',
        },
        {
          label: "Customer",
          field: "customer_name",
          thClass: "text-center",
          tdClass: "customer-name",
        },
        ...(window.companyPurpose !== 'rent' ? [{
          label: 'Supervisor',
          field: 'supervisor_name',
          thClass: "text-center",
        }] : []),
        {
          label: companyPurpose !== 'rent' ? 'Products & Services' : 'Unit',
          field: "unit",
          thClass: "text-center",
          filterOptions: {
            enabled: true,
            placeholder: companyPurpose !== 'rent'
              ? 'Name, SKU, barcode, service'
              : 'Id, name, SKU, barcode',
          },
        },

        /*{
          label: 'Time',
          field: 'time',
        },*/
        /*{
          label: "Package",
          field: "package_name",
          thClass: "text-left",
          tdClass: "text-left",
          filterOptions: {
            enabled: true,
            placeholder: "All",
            trigger: "enter",
            filterDropdownItems: [
              FULL_DAY_PACKAGE,
              THREE_FOURTHS_DAY_PACKAGE,
              HALF_DAY_PACKAGE,
              HARBOR_CRUISE,
            ],
          },
        },*/
        {
          label: "Status",
          field: "status",
          type: "number",
          thClass: "text-center",
          tdClass: "payment-status",
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: SALE_STATUSES.map((status) => ({
              value: status.id,
              text: status.name,
            })),
          },
        },
        {
          label: "Payment status",
          field: "payment_status",
          type: "number",
          thClass: "text-center",
          tdClass: "payment-status",
          filterOptions: {
            enabled: true,
            placeholder: 'All',
            filterDropdownItems: Object.values(SALE_PAYMENT_STATUSES).map((status) => ({
              value: status.id,
              text: status.name,
            })),
          },
        },

        /*{
          label: "Charter Survey",
          field: "survey",
          sortable: false,
          filterOptions: {
            enabled: true,
            placeholder: "All",
            filterDropdownItems: [
              {
                value: 0,
                text: "Not completed",
              },
              {
                value: 1,
                text: "Completed",
              },
            ],
            trigger: "enter",
          },
        },*/
        {
          label: 'Platform',
          field: 'platform',
          width: '100px',
          thClass: "text-center",
        },
        {
          label: "Action",
          field: "action",
          thClass: "text-center",
          tdClass: "text-center",
          sortable: false,
        }
      ],
      pageLength: 10,
      salesTablePage: 1,
      ordersLoadSeq: 0,
      searchDebounceTimer: null,
      suppressOrdersReload: false,
      edited: {
        id: null,
        unit: null,
        date: null,
        beforeDate: null,
        packageName: null,
        beforePackageName:null,
        status: null,
        statusBeforeChange: null,
        time: null,
        cancelled: false,
        package_id: null,
        details: null,
        unitObject: null,
        subtotal: null,
        tax: null,
        repeat: null,
        repeat_details: null,
        description: null,
        customer_note: null,
        products: [],
        services: [],
        address: null,
        discount: 0,
        time_interval: '',
        pickup: false,
        delivery: false,
        platform: null,
        id_on_platform: null,
        platform_id: null,
        platform_status: null,
        unit_deleted: false,
        has_remaining_to_ship: false,
        has_any_shipped: false,
        has_returns: false,
        has_any_returned: false,
        has_remaining_to_return: false,
        supervisor_id: null,
        supervisor_name: null,
        supervisor: null,
      },
      minDate: new Date(),
      blockDates: [],
      datepickerPlaceholder: "No date selected",
      isDatepickerDisabled: false,
      isPaymentHistoryLoading: true,
      paymentsHistoryFields: [
        {
          key: 'status',
          formatter: value => {
            return this.status[value].name
          }
        },
        'date',
        'amount',
        'method',
      ],
      paymentsHistory: [],
      alreadyPaid: 0,
      refundedAmount:0,
      totalPrice: 0,
      remainingToPay: 0,
      bookingLogs:null,
      paymentStatus: null,
      customer: {
        name: "",
        email: "",
        phone: "",
        stripe_id: ""
      },
      paymentTypes: [
        {
          text: 'Deposit',
          value: 1,
        },
        {
          text: 'Installment',
          value: 2,
        },
        {
          text: 'Final payment',
          value: 3,
        },
        {
          text: 'Overpayment',
          value: 4,
        },
      ],
      payment: {
        saleId: null,
        type: null,
        amount: 0,
        method: null,
      },
      isAddPaymentLoading: false,
      firstApril2022: new Date(2022, 4, 1),
      thisUnitPackageNameId:[],
      refundAmountShowHide: {},
      mobileRefundPayment: null,
    };
  },
  computed: {
    ...mapState('app', ['windowWidth']),
    ...mapGetters({
      sales: "sales/sales",
      salesWithPayments: "sales/salesWithPayments",
      salesWithPaymentsTotal: "sales/salesWithPaymentsTotal",
      units: 'units/units',
      companySettings: 'companies/companySettings',
      unitCategories: 'unitCategories/unitCategories',
      authUser: 'auth/user',
      staff: 'staff/staff',
    }),
    companyCountryIdFallback() {
      return window.companyCountryId
    },
    isCompanyOwner() {
      return this.authUser?.role === 'Company Owner'
    },
    isAdmin() {
      return this.authUser?.role === 'Admin'
    },
    isStaff() {
      return this.authUser?.role === 'Supervisor'
    },
    canCreateOrders() {
      return canStaffCreate(this.authUser, 'orders')
    },
    canChangeOrders() {
      return canStaffChange(this.authUser, 'orders')
    },
    canViewOtherSupervisorsOrders() {
      return canStaffViewOtherSupervisorsOrders(this.authUser)
    },
    canChangeOtherSupervisorsOrders() {
      return canStaffChangeOtherSupervisorsOrders(this.authUser)
    },
    canSeeAllSupervisorsOrders() {
      return this.isCompanyOwner
        || this.canViewOtherSupervisorsOrders
        || this.canChangeOtherSupervisorsOrders
    },
    canChangeEditedOrder() {
      return this.canChangeThisSale({
        status: this.edited?.statusBeforeChange ?? this.edited?.status,
        supervisor_id: this.edited?.supervisor_id,
      })
    },
    isPaymentHistoryCompact() {
      return Number(this.windowWidth || 0) < 992
    },
    paymentColumnsDisplay() {
      if (this.isPaymentHistoryCompact) {
        const keep = new Set(['paid', 'amount', 'method', 'date'])
        return [
          ...this.paymentColumns.filter((column) => keep.has(column.field)),
          {
            label: '',
            field: 'mobile_view',
            sortable: false,
            width: '48px',
            thClass: 'text-center',
            tdClass: 'text-center',
          },
        ]
      }
      return this.paymentColumns
    },
    paymentTableSelectOptions() {
      return {
        enabled: this.canChangeEditedOrder && !this.isPaymentHistoryCompact,
        disabled: !this.canChangeEditedOrder || this.isPaymentHistoryCompact,
        selectOnCheckboxOnly: true,
        selectionInfoClass: 'custom-class',
        selectionText: 'rows selected',
        clearSelectionText: 'clear',
        disableSelectInfo: true,
        selectAllByGroup: false,
      }
    },
    mobileRefundPaymentTypeLabel() {
      if (!this.mobileRefundPayment) {
        return ''
      }
      const type = this.paymentTypes.find(
        (pt) => pt.value === this.mobileRefundPayment.status,
      )
      return type?.text || this.mobileRefundPayment.status || ''
    },
    statusOptionsForEdit() {
      if (this.companyPurpose !== 'rent' && this.isStaff) {
        return this.statuses.filter((status) => status.id !== 0)
      }
      return this.statuses
    },
    showStaffNewStatusHint() {
      return this.companyPurpose !== 'rent'
        && this.isStaff
        && Number(this.edited.statusBeforeChange) === 0
    },
    editedTaxPercent() {
      if (this.edited?.tax != null && this.edited.tax !== '') {
        return this.edited.tax
      }
      return this.saleTax?.value ?? 0
    },
    hasConnectedPlatform() {
      return !!(this.companySettings && this.companySettings.has_connected_platform)
    },
    supervisorPictureFallback() {
      return normalizeImageUrl('')
    },
    canChangeOrderSupervisor() {
      return this.companyPurpose !== 'rent'
        && (this.isCompanyOwner || this.canChangeOtherSupervisorsOrders)
        && !this.isOrderEditLocked
    },
    showOrderSupervisorBlock() {
      return this.companyPurpose !== 'rent'
        && (this.editedHasSupervisor || this.canChangeOrderSupervisor)
    },
    editedHasSupervisor() {
      return this.edited?.supervisor_id != null
        && this.edited?.supervisor_id !== ''
        && !!this.edited?.supervisor_name
    },
    editedSupervisorPicture() {
      if (!this.editedHasSupervisor) {
        return this.supervisorPictureFallback
      }
      return normalizeImageUrl(this.edited?.supervisor || '')
    },
    supervisorChangeOptions() {
      const staffMembers = (this.staff || []).filter((member) => Number(member.inactive) !== 1)
      const withChangePermission = staffMembers.filter((member) => Boolean(member.permissions?.orders?.change))
      const options = withChangePermission.map((member) => ({
        id: member.id,
        name: member.name,
        picture: member.picture ?? null,
      }))

      const currentId = this.edited?.supervisor_id
      if (currentId != null && currentId !== '' && !options.some((option) => String(option.id) === String(currentId))) {
        const current = staffMembers.find((member) => String(member.id) === String(currentId))
        if (current) {
          options.unshift({
            id: current.id,
            name: current.name,
            picture: current.picture ?? null,
          })
        } else if (this.edited.supervisor_name) {
          options.unshift({
            id: currentId,
            name: this.edited.supervisor_name,
            picture: this.edited.supervisor ?? null,
          })
        }
      }

      return options
    },
    supervisorFilterOptions() {
      return [
        { id: '', name: 'All supervisors', picture: null },
        ...(this.staff || []).map((member) => ({
          id: member.id,
          name: member.name,
          picture: member.picture ?? null,
        })),
      ]
    },
    filteredSalesWithPayments() {
      return this.salesWithPayments || []
    },
    categoryFilterOptions() {
      return buildHierarchicalCategoryOptions(this.unitCategories, {
        allOption: { id: null, name: 'All categories' },
      })
    },
    hasActiveOrderFilters() {
      return Boolean(this.filterDateFrom)
        || Boolean(this.filterDateTo)
        || this.filterCategoryId != null
        || Boolean(this.filterDeliveryDate)
        || this.filterStatusId !== ''
        || this.filterPaymentStatusId !== ''
        || String(this.filterUnitKeyword || '').trim() !== ''
        || String(this.filterTotalFrom || '').trim() !== ''
        || String(this.filterTotalTo || '').trim() !== ''
        || this.filterSupervisorId !== ''
        || String(this.searchTerm || '').trim() !== ''
    },
    acceptPartialPayments() {
      return !!(this.companySettings && this.companySettings.accept_partial_payments)
    },
    orderPaymentStatusId() {
      const status = this.edited && this.edited.payment_status
      if (status && typeof status === 'object' && status.id !== undefined) {
        return Number(status.id)
      }
      return null
    },
    isOrderCancelled() {
      return Number(this.edited?.status) === SALE_STATUS_CANCELLED
    },
    isOrderFullyPaid() {
      return this.orderPaymentStatusId === 3 || this.orderPaymentStatusId === 4
    },
    lockPaymentAmountToDue() {
      if (this.acceptPartialPayments) {
        return false
      }
      if (this.remainingToPay > 0) {
        return true
      }
      return this.orderPaymentStatusId === 0
    },
    paymentAmountDue() {
      return parseFloat(this.remainingToPay) || 0
    },
    canSubmitAddPayment() {
      return (parseFloat(this.payment.amount) || 0) > 0
    },
    isOrderEditLocked() {
      return isSaleEditLocked(this.edited.statusBeforeChange)
    },
    hasDeletedOrderProducts() {
      if (this.edited.unit_deleted) {
        return true
      }

      return (this.edited.products || []).some((product) => product.deleted && product.items_quantity > 0)
    },
    hasEditedActiveProducts() {
      return (this.edited.products || []).some((product) => Number(product.items_quantity) > 0)
    },
    hasEditedActiveServices() {
      return (this.edited.services || []).some((service) => Number(service.items_quantity) > 0)
    },
    isEditedServicesOnly() {
      return this.companyPurpose !== 'rent'
        && !this.hasEditedActiveProducts
        && this.hasEditedActiveServices
    },
    editOrderDateLabel() {
      if (this.companyPurpose === 'rent') {
        return 'Start Date'
      }
      if (this.isEditedServicesOnly) {
        return 'Delivery date'
      }
      return (this.edited.pickup ? 'Pickup' : 'Delivery') + ' date'
    },
    editOrderTimeIntervalLabel() {
      if (this.companyPurpose === 'rent') {
        return 'Time interval'
      }
      if (this.isEditedServicesOnly) {
        return 'Delivery time interval'
      }
      return (this.edited.pickup ? 'Pickup' : 'Delivery') + ' time interval'
    },
    canSaveOrderEdit() {
      if (!this.canChangeEditedOrder) {
        return false
      }
      if (this.hasDeletedOrderProducts && this.editAction === 'status') {
        return false
      }
      if (this.isStaff && this.showStaffNewStatusHint && !this.hasStaffChangedOrderStatus) {
        return false
      }
      return !this.isOrderEditLocked || this.editAction === 'status'
    },
    hasStaffChangedOrderStatus() {
      if (this.showStaffNewStatusHint) {
        return this.edited.status !== null && this.edited.status !== undefined
      }

      return Number(this.edited.status) !== Number(this.edited.statusBeforeChange)
    },
    editStatusContextLabels() {
      const statusId = this.edited?.status ?? this.edited?.statusBeforeChange
      const labels = []

      if (shouldDisplaySaleStatusAsPartlyShipped(statusId, this.edited)) {
        labels.push({
          key: 'partly-shipped',
          text: 'Partly Shipped',
          icon: 'TruckIcon',
          className: 'order-status-context-label--partly-shipped',
        })
      } else if (shouldDisplaySaleStatusAsShipped(statusId, this.edited)) {
        labels.push({
          key: 'shipped',
          text: 'Shipped',
          icon: 'TruckIcon',
          className: 'order-status-context-label--shipped',
        })
      }

      if (shouldDisplaySaleStatusAsPartlyReturned(statusId, this.edited)) {
        labels.push({
          key: 'partly-returned',
          text: 'Partly Returned',
          icon: 'RotateCcwIcon',
          className: 'order-status-context-label--returned',
        })
      } else if (shouldDisplaySaleStatusAsReturned(statusId, this.edited)) {
        labels.push({
          key: 'returned',
          text: 'Returned',
          icon: 'RotateCcwIcon',
          className: 'order-status-context-label--returned',
        })
      }

      return labels
    },
    isOrderFieldsReadOnly() {
      if (!this.canChangeEditedOrder) {
        return true
      }
      return this.isOrderEditLocked && this.editAction !== 'status'
    },
    isStatusFieldEditable() {
      if (!this.canChangeEditedOrder) {
        return false
      }
      if (this.hasDeletedOrderProducts) {
        return false
      }
      return this.editAction === 'status' || !this.isOrderEditLocked
    },
    isFieldReadOnly() {
      return this.isOrderFieldsReadOnly || !!this.edited.platform
    },
    editModalTitle() {
      const label = this.companyPurpose !== 'rent' ? 'order' : 'booking'
      const prefix = (!this.canChangeEditedOrder || (this.isOrderEditLocked && this.editAction === 'all')) ? 'View' : 'Edit'
      const dbSuffix = this.isAdmin && this.edited.internal_id ? ` (DB #${this.edited.internal_id})` : ''
      return `${prefix} ${label} details #${this.edited.id}${dbSuffix}`
    },
    tableSearchOptions() {
      return { enabled: false }
    },
    tablePaginationOptions() {
      return {
        enabled: true,
        perPage: this.pageLength,
        perPageDropdownEnabled: false,
      }
    },
    tableSortOptions() {
      return {
        enabled: true,
        initialSortBy: {
          field: 'created_at',
          type: 'desc',
        },
      }
    },
    tableColumns() {
      let baseColumns = this.hasConnectedPlatform
        ? this.columns
        : this.columns.filter(column => column.field !== 'platform')

      if (this.companyPurpose !== 'rent' && this.isStaff && !this.canSeeAllSupervisorsOrders) {
        baseColumns = baseColumns.filter(column => column.field !== 'supervisor_name')
      }

      return [
        ...baseColumns.slice(0, 2),
        ...(this.companyPurpose !== 'rent'
          ? [{
              label: 'Type',
              field: 'pickup',
              type: 'string',
              thClass: "text-center",
              tdClass: "text-center",
              formatFn: value => (value ? 'Pick up' : 'Delivery'),
              filterOptions: {
                enabled: true,
                customFilter: true,
              },
            }]
          : []),
        ...baseColumns.slice(2),
      ]
    },
    guestsOptions(){
      const options = [];
      for (let i = 1; i <= 12; i++) {
        options.push(i);
      }
      return options;
    },
    packageNames() {

      const packages = ['qaq'];

      if( this.units.length > 0 && this.edited.unit != null ){

        const selectedUnitPackageNameId = [];
        this.units.packages.map(function(unitPackage){
          selectedUnitPackageNameId[unitPackage.name] = unitPackage.id;
          packages.push(unitPackage.name);
        });

        this.thisUnitPackageNameId = selectedUnitPackageNameId;
      }

      return packages
    },
    times() {
      const timeOptions = [
        {
          value: "06:00:00",
          text: "6 AM",
          selectable: isTimeSelectable("6 AM", this.blockDates, this.edited),
        },
        {
          value: "12:00:00",
          text: "12 PM",
          selectable: isTimeSelectable("12 PM", this.blockDates, this.edited),
        }
      ];

      return timeOptions;
    },
    customerId() {
      return this.$route.params.customerId ?? null;
    },
    pageHeading() {
      const label = this.companyPurpose !== 'rent' ? 'Orders' : 'Bookings'

      if (!this.customerId) {
        if (this.withReturnsOnly) {
          return this.companyPurpose !== 'rent' ? 'Orders with returns' : 'Bookings with returns'
        }

        return `All ${label}`
      }

      if (this.filterCustomerName) {
        return `${this.filterCustomerName} ${label}`
      }

      return label
    },
    repeatsOnly() {
      return this.$route.path.includes('repeat');
    },
    withReturnsOnly() {
      return this.$route.meta.withReturns === true
    },
  },
  watch: {
    searchTerm() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.scheduleOrdersReload(300)
    },
    filterCategoryId() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterDateFrom() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterDateTo() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterTotalFrom() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.scheduleOrdersReload(300)
    },
    filterTotalTo() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.scheduleOrdersReload(300)
    },
    filterSupervisorId() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterStatusId() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterPaymentStatusId() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    filterUnitKeyword() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.scheduleOrdersReload(300)
    },
    filterDeliveryDate() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    pageLength() {
      this.salesTablePage = 1
      if (this.isTableLoading || this.suppressOrdersReload) {
        return
      }
      this.reloadOrdersTable()
    },
    customerId: {
      immediate: true,
      handler(customerId, previousCustomerId) {
        this.loadFilterCustomerName(customerId)

        if (previousCustomerId === undefined) {
          return
        }

        if (String(customerId || '') === String(previousCustomerId || '')) {
          return
        }

        this.salesTablePage = 1
        if (this.isTableLoading) {
          return
        }
        this.reloadOrdersTable()
      },
    },
    '$route.path'(newPath, oldPath) {
      if (!oldPath) {
        return
      }

      this.salesTablePage = 1
      this.reloadOrdersTable()
    },
    salesWithPayments(rows) {
      if (!this.customerId || this.filterCustomerName || !rows?.length) {
        return
      }

      this.filterCustomerName = rows[0].customer_name || null
    },
    'payment.method': {
      immediate: true,
      handler(newMethod) {

        if (!newMethod) return;

        if (!this.paymentMethods.includes(newMethod)) {
          this.paymentMethods.push(newMethod)
        }
      }
    },

    'edited.pickup': function () {

      if (this.edited.pickup) {
        this.edited.delivery = false;
        this.edited.shipping = 0;
        this.showShipping = false;
      }
    },
    'edited.delivery': function () {

      if (this.edited.delivery) {
        this.edited.pickup = false;
        this.showShipping = true;
        // Pickup orders store shipping flags as false; recalculate when switching to delivery.
        if (!this.edited.has_any_shipped) {
          this.edited.has_remaining_to_ship = (this.edited.products || []).some(
            (product) => Number(product.items_quantity) > 0 && !product.deleted,
          )
        }
      }
    },
    'edited.unitObject.id': function () {
        this.loadBlockDates();
    },
    searchTerm(value) {
        this.changeUrl({ search: value });
    },
    'edited.products' : function () {
      this.recalculateEditedSubtotal()
    },
    'edited.services' : function () {
      this.recalculateEditedSubtotal()
    },
    isEditedServicesOnly(value) {
      if (value) {
        this.edited.pickup = true
        this.edited.delivery = false
        this.edited.shipping = 0
        this.showShipping = false
      } else if (this.companyPurpose !== 'rent') {
        this.showShipping = !this.edited.pickup
      }
    },
    'edited.shipping'(value) {
      if (value === null || value === '') {
        return;
      }
      const rounded = this.$moneyRound(value);
      if (Number(value) !== rounded) {
        this.edited.shipping = rounded;
      }
    }
  },
  async mounted() {

    await this.loadCompanySettings().catch(() => {})
    if (this.companyPurpose !== 'rent') {
      await this.loadUnitCategories().catch(() => {})
      if (this.canSeeAllSupervisorsOrders) {
        await this.loadStaff().catch(() => {})
      }
    }

    /* eslint-disable-next-line */
    for (const [key, value] of Object.entries(this.$route.query)) {
      switch (key) {
        case "search":
          this.searchTerm = value;
          break;
        case "unit":
          this.filterUnitKeyword = value;
          break;
        case "package_name":
          this.$set(this.columns[5].filterOptions, "filterValue", value);
          break;
        default:
      }
    }

    await this.reloadOrdersTable().catch(() => {
      toastFailure("Can not get bookings");
    });

    // Rent edit modal needs the full units list for the unit picker.
    // Shop orders already include products on each sale from salesWithPayments.
    if (this.companyPurpose === 'rent') {
      await this.loadUnits().catch(() => {
        toastFailure("Can not get units");
      });
    }

    this.isTableLoading = false;
  },
  validations() {
    return {
      edited: {
        payment_status: { required },
        date: { required },
        packageName: { required: requiredIf(function() {
            return this.edited.subtotal == null && this.edited.duration == null && companyPurpose === 'rent' ? true : false;
          })
        },
        subtotal: { numeric, required: requiredIf(function() {
            return this.edited.packageName == null && companyPurpose === 'rent' ? true : false;
          })
        },
        shipping: { numeric, minValue: minValue(0) },
        discount: { numeric, minValue: minValue(0) },
        duration: { numeric, required: requiredIf(function() {
            return this.edited.packageName == null && companyPurpose === 'rent' ? true : false;
          })
        },
        address : {
          country_id: { required: requiredIf(function() {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          },
          zip_code: { numeric, required: requiredIf(function() {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          },
          region_id: { required: requiredIf(function() {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          },
          city: { required: requiredIf(function() {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          },
          address_line_1: { required: requiredIf(function () {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          },
          name: { required: requiredIf(function () {
              return this.edited.pickup === false && companyPurpose !== 'rent' ? true : false;
            })
          }
        }
      }
    }

  },
  setup() {

    const vSale = useVuelidate({ $scope: 'salesForm' });
    return { vSale };
  },
  methods: {
    supervisorPictureUrl(picture) {
      return normalizeImageUrl(picture || '')
    },
    toggleSupervisorChange() {
      this.showSupervisorChange = !this.showSupervisorChange
      if (this.showSupervisorChange && this.canChangeOrderSupervisor) {
        this.loadStaff().catch(() => {})
      }
    },
    onSupervisorChangeSelect(supervisorId) {
      const selected = this.supervisorChangeOptions.find(
        (option) => String(option.id) === String(supervisorId),
      )
      this.edited.supervisor_id = supervisorId ?? null
      this.edited.supervisor_name = selected?.name ?? null
      this.edited.supervisor = selected?.picture ?? null

      if (supervisorId != null && supervisorId !== '') {
        const currentStatus = this.edited.status ?? this.edited.statusBeforeChange
        if (Number(currentStatus) === 0) {
          this.edited.status = 10
        }
      }
    },
    canChangeThisSale(sale) {
      if (!this.canChangeOrders) {
        return false
      }
      if (!this.isStaff || this.companyPurpose === 'rent') {
        return true
      }
      if (!sale) {
        return false
      }
      if (this.canChangeOtherSupervisorsOrders) {
        return true
      }
      // Assigned to this staff member
      if (
        sale.supervisor_id != null
        && sale.supervisor_id !== ''
        && String(sale.supervisor_id) === String(this.authUser?.id)
      ) {
        return true
      }
      // Unassigned and not completed/cancelled — take control on edit
      if (sale.supervisor_id == null || sale.supervisor_id === '') {
        return !isSaleEditLocked(sale.status)
      }
      return false
    },
    canEditSaleFromActions(sale) {
      return this.canChangeThisSale(sale) && !isSaleEditLocked(sale?.status)
    },
    ...mapActions({
      loadSalesWithPayments: "sales/loadSalesWithPayments",
      loadUnitCategories: 'unitCategories/loadUnitCategories',
      updateSale: "sales/updateSale",
      addSalePayment: "sales/addSalePayment",
      resendEmail: "sales/resendEmail",
      sendInvoiceToCustomer: "sales/sendInvoice",
      repeatSale: "sales/repeat",
      refundSalePayment: "sales/refundSalePayment",
      deleteSale: "sales/deleteSale",
      loadUnits: 'units/loadUnits',
      loadCompanySettings: 'companies/loadCompanySettings',
      loadCustomer: 'customers/loadCustomer',
      loadStaff: 'staff/loadStaff',
      loadSale: 'sales/sale',
    }),
    async loadFilterCustomerName(customerId) {
      this.filterCustomerName = null

      if (!customerId) {
        return
      }

      try {
        const customer = await this.loadCustomer(customerId)
        this.filterCustomerName = customer?.name || null
      } catch {
        const rows = this.salesWithPayments || []
        this.filterCustomerName = rows[0]?.customer_name || null
      }
    },
    greaterThan(min) {
      // Returns a validator function
      return value => {
        // Check the condition based on other form fields
        if (this.edited.payment_status.id === 2 || this.edited.payment_status.id === 3) {
          return value > min; // Value must be greater than zero
        }
        return true; // No validation if condition is not met
      };
    },
    calcTotal () {

      if (this.edited.discount > 99) {
        this.edited.discount = 99;
      }

      if (this.edited.discount < 0) {
        this.edited.discount = 0;
      }

      return this.$calcSaleTotal(this.edited);
    },
    getStatus(statusId) {
      return Object.values(this.statuses).find(s => s.id === statusId) ?? {
        id: -2,
        name: "Unknown",
        class: "yellow",
      };
    },
    getStatusDisplay(statusId, booking) {
      return getSaleStatusDisplay(statusId, booking, this.statuses);
    },
    bookingCreatedDate(row) {
      if (!row?.created_at) {
        return null
      }
      const m = parseApiInstant(row.created_at)
      return m && m.isValid() ? m.format('YYYY-MM-DD') : null
    },
    formatSaleCivilDate(value) {
      return formatCivilDate(value) || value || ''
    },
    saleRepeatType(row) {
      const repeat = String(row?.repeat || '').trim()
      if (!repeat || !repeat.includes('=')) {
        return null
      }

      const type = repeat.split('=')[0].trim()
      const allowed = ['Yearly', 'Weekly', 'Monthly', 'Daily']

      return allowed.includes(type) ? type : null
    },
    formatInstant(value) {
      return formatCompanyDateTime(value) || value || ''
    },
    getCategoryIdsWithDescendants(categoryId) {
      const categories = this.unitCategories || []
      const ids = [Number(categoryId)]

      const collectDescendants = (parentId) => {
        categories
          .filter((category) => Number(category.parent_category_id) === Number(parentId))
          .forEach((category) => {
            ids.push(Number(category.id))
            collectDescendants(category.id)
          })
      }

      collectDescendants(categoryId)
      return ids
    },
    bookingMatchesCategory(row, categoryIds) {
      const products = row.products || []
      return products.some((product) => categoryIds.includes(Number(product.unit_category_id)))
    },
    resolveUnitObject(row) {
      if (!row?.unit_id || Number(row.unit_id) <= 0) {
        return null
      }

      const fromProducts = (row.products || []).find(
        (product) => Number(product.id) === Number(row.unit_id),
      )
      if (fromProducts) {
        return fromProducts
      }

      if (this.companyPurpose === 'rent') {
        return (this.units || []).find((item) => Number(item.id) === Number(row.unit_id)) || null
      }

      return {
        id: row.unit_id,
        name: row.unit || '',
      }
    },
    bookingMatchesUnitSearch(row, term) {
      const values = new Set()

      const addValue = (value) => {
        if (value !== null && value !== undefined && String(value).trim() !== '') {
          values.add(String(value).toLowerCase())
        }
      }

      addValue(row.unit)
      addValue(row.unit_id)

      ;(row.products || []).forEach((product) => {
        addValue(product.id)
        addValue(product.name)
        addValue(product.sku)
        addValue(product.barcode)
      })

      ;(row.services || []).forEach((service) => {
        addValue(service.id)
        addValue(service.name)
      })

      return Array.from(values).some((value) => value.includes(term))
    },
    buildBookingSearchText(row) {
      const statusDisplay = this.getStatusDisplay(row.status, row).name
      const statusBase = this.getStatus(row.status).name
      const paymentStatus = this.paymentStatuses[row.payment_status]?.name || ''
      const productText = (row.products || []).map((product) => [
        product.id,
        product.name,
        product.sku,
        product.items_quantity,
        product.price,
      ].filter(Boolean).join(' ')).join(' ')
      const serviceText = (row.services || []).map((service) => [
        service.id,
        service.name,
        service.items_quantity,
        service.price,
      ].filter(Boolean).join(' ')).join(' ')

      return [
        row.id,
        row.customer_name,
        row.customer_email,
        row.phone,
        row.unit,
        row.unit_id,
        row.date,
        row.time,
        row.time_interval,
        row.package_name,
        row.total,
        row.subtotal,
        row.tax_total,
        row.discount_total,
        row.shipping,
        row.platform_status,
        row.description,
        row.customer_note,
        row.details,
        row.id_on_platform,
        statusDisplay,
        statusBase,
        paymentStatus,
        this.isServicesOnlyOrder(row) ? 'Service' : (row.pickup ? 'Pick up' : 'Delivery'),
        row.platform?.name,
        row.platform?.type,
        row.created_at,
        productText,
        serviceText,
        this.$formatCurrency ? this.$formatCurrency(this.$resolveSaleTotal(row)) : row.total,
      ].filter((value) => value !== null && value !== undefined && String(value).trim() !== '').join(' ')
    },
    bookingMatchesSearch(row, term) {
      return this.buildBookingSearchText(row).toLowerCase().includes(term)
    },
    parseFilterTotal(value) {
      const trimmed = String(value ?? '').trim()
      if (trimmed === '') {
        return null
      }

      const num = Number.parseFloat(trimmed)
      if (Number.isNaN(num) || num < 0) {
        return null
      }

      return num
    },
    onFilterSupervisorChange(value) {
      this.filterSupervisorId = value
      this.salesTablePage = 1
    },
    buildOrdersListFilters() {
      const totalFrom = this.parseFilterTotal(this.filterTotalFrom)
      const totalTo = this.parseFilterTotal(this.filterTotalTo)

      return {
        date_from: this.filterDateFrom || undefined,
        date_to: this.filterDateTo || undefined,
        delivery_date: this.filterDeliveryDate || undefined,
        status: this.filterStatusId !== '' ? this.filterStatusId : undefined,
        payment_status: this.filterPaymentStatusId !== '' ? this.filterPaymentStatusId : undefined,
        supervisor_id: this.filterSupervisorId !== '' ? this.filterSupervisorId : undefined,
        category_id: this.filterCategoryId != null && this.filterCategoryId !== ''
          ? this.filterCategoryId
          : undefined,
        unit_keyword: String(this.filterUnitKeyword || '').trim() || undefined,
        search: String(this.searchTerm || '').trim() || undefined,
        total_from: totalFrom != null ? totalFrom : undefined,
        total_to: totalTo != null ? totalTo : undefined,
        sort_by: this.tableSort?.field || 'created_at',
        sort_dir: this.tableSort?.type || 'desc',
      }
    },
    scheduleOrdersReload(delayMs = 300) {
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
      }
      this.searchDebounceTimer = setTimeout(() => {
        this.reloadOrdersTable()
      }, delayMs)
    },
    reloadOrdersTable() {
      const seq = ++this.ordersLoadSeq

      return this.loadSalesWithPayments({
        userId: this.customerId,
        repeats: this.repeatsOnly,
        withReturns: this.withReturnsOnly,
        page: this.salesTablePage,
        perPage: this.pageLength,
        filters: this.buildOrdersListFilters(),
      }).then((result) => {
        if (seq !== this.ordersLoadSeq) {
          return result
        }

        const total = Number(result?.total ?? 0)
        const maxPage = Math.max(1, Math.ceil(total / this.pageLength) || 1)
        if (this.salesTablePage > maxPage) {
          this.salesTablePage = maxPage
          return this.reloadOrdersTable()
        }

        return result
      }).catch((error) => {
        if (seq !== this.ordersLoadSeq) {
          return
        }
        toastFailure('Can not get bookings')
        throw error
      })
    },
    resetOrderFilters() {
      this.suppressOrdersReload = true
      this.filterDateFrom = null
      this.filterDateTo = null
      this.filterCategoryId = null
      this.salesTablePage = 1
      this.filterDeliveryDate = null
      this.filterStatusId = ''
      this.filterPaymentStatusId = ''
      this.filterUnitKeyword = ''
      this.filterTotalFrom = ''
      this.filterTotalTo = ''
      this.filterSupervisorId = ''
      this.tableSort = { field: 'created_at', type: 'desc' }
      this.searchTerm = ''
      if (this.searchDebounceTimer) {
        clearTimeout(this.searchDebounceTimer)
        this.searchDebounceTimer = null
      }
      if (this.$refs.salesTable && this.$refs.salesTable.reset) {
        this.$refs.salesTable.reset()
      }
      this.suppressOrdersReload = false
      this.reloadOrdersTable()
    },
    onColumnSelectFilter(field, event) {
      this[field] = event.target.value
    },
    shouldShowShippingStatusLabel(statusId, booking) {
      return shouldDisplayShippingStatusLabel(statusId, booking);
    },
    shouldShowReturnStatusLabel(statusId, booking) {
      return shouldDisplayReturnStatusLabel(statusId, booking);
    },
    openPrintModal(row, printType) {
      const addressRequiredPrints = ['invoice', 'packing_slip', 'delivery_note'];
      if (addressRequiredPrints.includes(printType) && !hasCompanyPrintAddress(this.companySettings)) {
        sweetWarning(
          companyPrintAddressMissingTitle(printType),
          COMPANY_PRINT_ADDRESS_MISSING_BODY,
        );
        return;
      }

      this.saleToPrintId = row.id;
      this.printType = printType;
      this.clickDate = new Date();
    },
    openShippingsList(row) {
      if (row.pickup) {
        return
      }
      this.shippingSale = row
      this.shippingsListClickDate = new Date()
    },
    openCreateShipping(row) {
      if (!this.canChangeThisSale(row)) {
        this.openShippingsList(row)
        return
      }
      if (row.pickup) {
        toastFailure('Shipping can only be created for delivery orders.')
        return
      }
      this.shippingSale = row
      this.selectedShippingId = null
      this.manageShippingClickDate = new Date()
    },
    isProductOrder(row) {
      return !row?.unit_id || Number(row.unit_id) === 0
    },
    orderHasActiveProducts(row) {
      return (row?.products || []).some((product) => Number(product.items_quantity) > 0)
    },
    orderHasActiveServices(row) {
      return (row?.services || []).some((service) => Number(service.items_quantity) > 0)
    },
    orderLineItemsPreview(row) {
      const products = (row?.products || [])
        .filter((product) => Number(product.items_quantity) > 0)
        .map((product) => ({
          ...product,
          line_type: 'product',
        }))
      const services = (row?.services || [])
        .filter((service) => Number(service.items_quantity) > 0)
        .map((service) => ({
          ...service,
          line_type: 'service',
        }))

      return [...products, ...services]
    },
    orderLineItemsFontClass(row) {
      const visibleCount = Math.min(this.orderLineItemsPreview(row).length, 4)
      return `order-lines-font-${Math.max(visibleCount, 1)}`
    },
    isServicesOnlyOrder(row) {
      return this.companyPurpose !== 'rent'
        && !this.orderHasActiveProducts(row)
        && this.orderHasActiveServices(row)
    },
    openReturnsModal(row) {
      if (!this.isProductOrder(row)) {
        return
      }
      this.returnsSale = row
      this.returnsClickDate = new Date()
    },
    onReturnSaved(payload) {
      if (!payload || !this.returnsSale) {
        return
      }
      this.updateSaleReturnFlags(this.returnsSale.id, payload)
    },
    updateSaleReturnFlags(saleId, payload) {
      const applyFlags = (booking) => {
        if (!booking) {
          return
        }
        if (payload.has_returns !== undefined) {
          booking.has_returns = payload.has_returns
        }
        if (payload.has_any_returned !== undefined) {
          booking.has_any_returned = payload.has_any_returned
        }
        if (payload.has_remaining_to_return !== undefined) {
          booking.has_remaining_to_return = payload.has_remaining_to_return
        }
      }

      applyFlags(this.salesWithPayments.find((item) => item.id === saleId))
      if (this.edited && this.edited.id === saleId) {
        applyFlags(this.edited)
      }
      if (this.returnsSale && this.returnsSale.id === saleId) {
        applyFlags(this.returnsSale)
      }
    },
    openManageShippingEdit({ sale, booking, shippingId }) {
      this.shippingSale = sale || booking
      this.selectedShippingId = shippingId
      this.manageShippingClickDate = new Date()
    },
    onShippingRemainingChanged(payload) {
      if (!payload || !this.shippingSale) {
        return
      }
      this.updateSaleShippingFlags(this.shippingSale.id, payload)
    },
    onShippingSaved(payload) {
      if (payload && this.shippingSale) {
        this.updateSaleShippingFlags(this.shippingSale.id, payload)
        if (payload.sale_status !== undefined && payload.sale_status !== null) {
          this.updateSaleStatus(this.shippingSale.id, payload.sale_status)
        }
      }
      if (this.$refs.bookingShippings && this.$refs.bookingShippings.refresh) {
        this.$refs.bookingShippings.refresh()
      }
    },
    updateSaleShippingFlags(saleId, payload) {
      const booking = this.salesWithPayments.find((item) => item.id === saleId)
      if (booking) {
        if (payload.has_remaining_to_ship !== undefined) {
          booking.has_remaining_to_ship = payload.has_remaining_to_ship
        }
        if (payload.has_any_shipped !== undefined) {
          booking.has_any_shipped = payload.has_any_shipped
        }
      }
      if (this.edited.id === saleId) {
        if (payload.has_remaining_to_ship !== undefined) {
          this.edited.has_remaining_to_ship = payload.has_remaining_to_ship
        }
        if (payload.has_any_shipped !== undefined) {
          this.edited.has_any_shipped = payload.has_any_shipped
        }
      }
      if (this.shippingSale && this.shippingSale.id === saleId) {
        if (payload.has_remaining_to_ship !== undefined) {
          this.shippingSale.has_remaining_to_ship = payload.has_remaining_to_ship
        }
        if (payload.has_any_shipped !== undefined) {
          this.shippingSale.has_any_shipped = payload.has_any_shipped
        }
      }
    },
    updateSaleRemainingToShip(saleId, hasRemainingToShip) {
      this.updateSaleShippingFlags(saleId, { has_remaining_to_ship: hasRemainingToShip })
    },
    updateSaleStatus(saleId, status) {
      const booking = this.salesWithPayments.find((item) => item.id === saleId)
      if (booking) {
        booking.status = status
      }
      if (this.shippingSale && this.shippingSale.id === saleId) {
        this.shippingSale.status = status
      }
      if (this.edited.id === saleId) {
        this.edited.status = status
      }
    },
    updateProducts(updatedProducts) {
      this.edited.products = updatedProducts;
    },
    updateServices(updatedServices) {
      this.edited.services = updatedServices;
    },
    recalculateEditedSubtotal() {
      if (companyPurpose === 'rent') {
        return
      }

      let totalPrice = 0
      ;(this.edited.products || []).forEach((product) => {
        if (product.items_quantity > 0) {
          totalPrice = this.$moneyRound(totalPrice + product.items_quantity * product.price)
        }
      })
      ;(this.edited.services || []).forEach((service) => {
        if (service.items_quantity > 0) {
          totalPrice = this.$moneyRound(totalPrice + service.items_quantity * service.price)
        }
      })
      this.edited.subtotal = totalPrice
    },
    unitChanged() {

        const selectedUnitPackageNameId = [];
        const packagesLocal = [];
        let that = this;
        /*this.units[this.edited.unitObject.id - 1].packages.map(function(unitPackage){

            selectedUnitPackageNameId[unitPackage.name] = unitPackage.id;
            if( unitPackage.inactive === 0 ) {
              packagesLocal.push(unitPackage.name);
            }
        });*/

        this.thisUnitPackageNameId = selectedUnitPackageNameId;

        this.packages = packagesLocal;
    },
    hasInvoiceCustomer(row) {
      return this.hasNumericUserId(row)
    },
    hasNumericUserId(row) {
      const userId = row?.user_id

      if (userId == null || userId === '') {
        return false
      }

      const numericId = Number(userId)
      return Number.isFinite(numericId) && numericId > 0
    },
    async sendInvoice(saleId){
      if (!hasCompanyPrintAddress(this.companySettings)) {
        sweetWarning(
          companyPrintAddressMissingTitle('invoice', 'send'),
          COMPANY_PRINT_ADDRESS_MISSING_BODY,
        );
        return;
      }

      if (await this.confirmationMessage('Are you sure you want to send an invoice for ' + ((this.companyPurpose || companyPurpose) !== 'rent' ? 'order' : 'booking') + ' #'+saleId+'?')) {

        this.sendInvoiceToCustomer({
          id: saleId
        }).then(() => {
          toastSuccess("Invoice successfully sent");
        }).catch((error) => {
          const message = typeof error?.response?.data === 'string'
            ? error.response.data
            : (error?.response?.data?.message || 'Something went wrong');
          toastFailure(message);
        });
      }
    },
    async repeatThisSale(saleId){

      if (await this.confirmationMessage('Are you sure you want to repeat this ' + (companyPurpose !== 'rent' ? 'order' : 'booking') + ' #'+saleId+'?')) {

        this.repeatSale({
          id: saleId
        }).then(() => {
          toastSuccess("Successfully created");
        }).catch((error) => {
          console.log('invoice error', error);
          toastFailure("Something went wrong");
        }).finally(() => {

        });
      }
    },
    async resendSaleEmail(saleId){

      if (await this.confirmationMessage('Are you sure you want to resend an email for ' + (companyPurpose !== 'rent' ? 'order' : 'booking') + ' #'+saleId+'?')) {

        this.resendEmail({
          id: saleId
        }).then(() => {
          toastSuccess("Email successfully sent");
        }).catch((error) => {
          toastFailure("Something went wrong");
        }).finally(() => {

        });
      }
    },
    cellMoneyFormat(value) {
      return this.$formatCurrency(value, null);
    },
    showError(responseData=null, warning=false, title=null) {

      if( responseData != null && responseData.message != undefined && responseData.message != null ){

        let errorMSG = responseData.message + '</br>';
        if(responseData.errors){

          Object.keys(responseData.errors).map(function(errorKey){
            responseData.errors[errorKey].map(function(value, key){
              errorMSG += value + '</br>';
            });
          });
        }

        if( warning ){
          sweetWarning(title == null ? 'Warning' : title, errorMSG);
        }
        else{
          sweetError(title == null ? 'Error'  : title, errorMSG);
        }
      }
      else{
        toastFailure("Something went wrong.");
      }
    },
    uncheckRow(row){
      setTimeout(function(){
        row.vgtSelected = false;
      }, 100);
    },
    clickRowPayments(params){

      if( params.row.vgtDisabled === true ){

        this.showError({message: '<br>Payment #'+params.row.id+' has no transaction id in database'}, 1, 'Refund must be processed manually');
        this.uncheckRow(params.row);
      }
    },
    paymentRowsSelected(params) {

      if (params.selectedRows.length > 0) {

        let that = this;
        params.selectedRows.map(function (row) {

          if (row.vgtDisabled === false) {

            if (that.refundAmountShowHide[row.id] == undefined) {
              that.refundAmountShowHide[row.id] = { visible: 'hide', amount: row.amount };
            }

            if (that.refundAmountShowHide[row.id]['visible'] == undefined || that.refundAmountShowHide[row.id]['visible'] == 'hide') {
              that.refundAmountShowHide[row.id]['visible'] = 'show';
            } else {
              //that.refundAmountShowHide[row.id]['visible'] = 'hide';
            }

            that.refundAmountShowHide[row.id]['amount'] = row.amount;
          } else {
            that.uncheckRow(row);
          }

        });

        this.refundButtonDisabled = false;
      } else {

        let that = this;
        this.$refs['paymentsHistoryTable'].rows.map(function (row) {
          if (that.refundAmountShowHide[row.id] == undefined) {
            that.refundAmountShowHide[row.id] = { visible: 'hide', amount: 0 };
          }
          that.refundAmountShowHide[row.id]['visible'] = 'hide';
          that.uncheckRow(row);
        });

        this.refundButtonDisabled = true;
      }
    },
    onRefundAmountPaste(paymentId, maxAmount, event) {
      this.$nextTick(() => {
        this.clampRefundAmountInput(paymentId, maxAmount, event);
      });
    },
    clampRefundAmountInput(paymentId, maxAmount, eventOrValue) {
      const max = this.$moneyRound(parseFloat(maxAmount));
      const min = max > 0 ? 0.01 : 0;

      let raw;
      let inputEl = null;

      if (eventOrValue != null && typeof eventOrValue === 'object' && eventOrValue.target) {
        inputEl = eventOrValue.target;
        raw = inputEl.value;
      } else {
        raw = eventOrValue;
      }

      if (!this.refundAmountShowHide[paymentId]) {
        this.refundAmountShowHide[paymentId] = { visible: 'show', amount: max };
      }

      if (raw === '' || raw === null || raw === undefined) {
        this.refundAmountShowHide[paymentId].amount = '';
        if (inputEl) {
          inputEl.value = '';
        }
        return;
      }

      let value = this.$moneyRound(parseFloat(raw));
      if (Number.isNaN(value)) {
        this.refundAmountShowHide[paymentId].amount = '';
        if (inputEl) {
          inputEl.value = '';
        }
        return;
      }

      if (value > max) {
        value = max;
      } else if (value < min) {
        value = min;
      }

      this.refundAmountShowHide[paymentId].amount = value;
      if (inputEl) {
        inputEl.value = value;
      }
    },
    validateRefundAmounts(targetPayments = null) {
      const selectedRows = targetPayments
        || this.$refs.paymentsHistoryTable?.selectedRows
        || [];

      for (const row of selectedRows) {
        const maxAmount = this.$moneyRound(parseFloat(row.amount));
        const amount = this.$moneyRound(parseFloat(this.refundAmountShowHide[row.id]?.amount));
        const min = maxAmount > 0 ? 0.01 : 0;

        if (Number.isNaN(amount) || amount <= 0) {
          toastFailure(`The refund amount for payment #${row.id} must be greater than 0.`);
          return false;
        }

        if (amount < min || amount > maxAmount) {
          toastFailure(
            `Refund amount for payment #${row.id} must be between ${this.$formatCurrency(min)} and ${this.$formatCurrency(maxAmount)}.`
          );
          return false;
        }
      }

      return true;
    },
    openPaymentRefundDetail(row) {
      if (!row) {
        return
      }

      this.mobileRefundPayment = row
      const existing = this.refundAmountShowHide[row.id]
      this.$set(this.refundAmountShowHide, row.id, {
        visible: 'show',
        amount: existing?.amount != null && existing.amount !== ''
          ? existing.amount
          : row.amount,
      })
      this.$root.$emit('bv::show::modal', 'payment-refund-detail-modal')
    },
    onPaymentRefundDetailHidden() {
      this.mobileRefundPayment = null
    },
    checkMobileRefundPayment() {
      if (!this.mobileRefundPayment) {
        return
      }
      if (this.mobileRefundPayment.vgtDisabled) {
        this.showError(
          { message: `<br>Payment #${this.mobileRefundPayment.id} has no transaction id in database` },
          1,
          'Refund must be processed manually',
        )
        return
      }

      this.refundAlsoCancel = false
      if (!this.validateRefundAmounts([this.mobileRefundPayment])) {
        return
      }
      this.$root.$emit('bv::show::modal', 'refund-modal')
    },
    async checkRefundPayments(){

      this.refundAlsoCancel = false;
      if( this.$refs['paymentsHistoryTable'].selectedRows.length > 0 ){
        if (!this.validateRefundAmounts()) {
          return;
        }
        this.$root.$emit('bv::show::modal', 'refund-modal');
      }
    },
    getRefundTargetPayments() {
      if (this.isPaymentHistoryCompact && this.mobileRefundPayment) {
        return [this.mobileRefundPayment]
      }
      return this.$refs.paymentsHistoryTable?.selectedRows || []
    },
    refundPayments(bvModalEvt){
      const targetPayments = this.getRefundTargetPayments()
      if (!this.validateRefundAmounts(targetPayments)) {
        if (bvModalEvt) {
          bvModalEvt.preventDefault();
        }
        return;
      }

      this.refundButtonDisabled = true;

      let selectedPayments = [];
      let that = this;
      targetPayments.map(function(selectedPayment){

        selectedPayments.push({
          'id': selectedPayment.id,
          'amount': that.refundAmountShowHide[selectedPayment.id]['amount']
        });
      });

      this.refundSalePayment({
        sale_id: this.paymentsHistory[0].sale_id,
        selected_payments: selectedPayments,
        sale_canceled: this.refundAlsoCancel,
      }).then((response) => {
        toastSuccess("Payment successfully refunded");

        if (response.sale_payment_status !== undefined) {
          this.edited.payment_status = this.paymentStatuses[response.sale_payment_status];
        }
        if (this.refundAlsoCancel) {
          this.edited.cancelled = true;
          this.edited.status = SALE_STATUS_CANCELLED;
          this.edited.statusBeforeChange = SALE_STATUS_CANCELLED;
        }

        this.$root.$emit("bv::hide::modal", "payment-refund-detail-modal");
        this.$root.$emit("bv::hide::modal", "payment-history-modal");
        this.mobileRefundPayment = null
      }).catch((error) => {
        this.showError(error.response?.data || error);
      }).finally(() => {
        this.isAddPaymentLoading = false;
        this.refundButtonDisabled = false;
      });

    },
    setEdited(row) {

      this.edited.id = row.id;
      this.edited.unit = row.unit_id;
      this.edited.date = row.date;
      this.edited.beforeDate = row.date;
      this.edited.time = row.time;
      this.edited.packageName = row.package_name;
      this.edited.beforePackageName = row.package_name;
      this.edited.package_id = row.package_id;
      this.edited.payment_status = this.paymentStatuses[row.payment_status];
      this.edited.statusBeforeChange = row.status;
      if (this.companyPurpose !== 'rent' && this.authUser?.role === 'Supervisor' && Number(row.status) === 0) {
        this.edited.status = null;
      } else {
        this.edited.status = row.status;
      }
      this.edited.cancelled = Boolean(row.cancelled);
      this.edited.details = row.details;
      this.edited.subtotal = this.$moneyRound(row.subtotal);
      this.edited.shipping = this.$moneyRound(row.shipping);
      this.edited.tax = row.tax;
      this.edited.description = row.description;
      this.edited.customer_note = row.customer_note;
      this.edited.unitObject = this.resolveUnitObject(row);
      this.edited.unit_deleted = Boolean(row.unit_deleted);
      this.edited.products = row.products || [];
      this.edited.services = row.services || [];

      if (row.address != null) {
        this.edited.address = row.address;
      } else {
        this.edited.address = {
          id: null,
          country_id: window.companyCountryId,
          region_id: null,
          city: '',
          address_line_1: '',
          address_line_2: '',
          zip_code: '',
          name: '',
          phone: '',
          description: '',
          default: true
        };
      }

      this.edited.discount = row.discount;
      this.edited.time_interval = row.time_interval;
      this.edited.repeat = row.repeat !== null ? row.repeat.split('=')[0] : row.repeat;
      this.edited.repeat_details = row.repeat !== null ? row.repeat.split('=')[1] : '';

      if (row.pickup === 1) {
        this.edited.pickup = true;
        this.edited.delivery = false;
        this.showShipping = false;
      } else {
        this.edited.pickup = false;
        this.edited.delivery = true;
        this.showShipping = true;
      }

      this.edited.platform = row.platform;
      this.edited.platform_status = row.platform_status;
      this.edited.platform_id = row.platform_id;
      this.edited.id_on_platform = row.id_on_platform;
      this.edited.has_remaining_to_ship = row.has_remaining_to_ship ?? false;
      this.edited.has_any_shipped = row.has_any_shipped ?? false;
      this.edited.has_returns = row.has_returns ?? false;
      this.edited.has_any_returned = row.has_any_returned ?? false;
      this.edited.has_remaining_to_return = row.has_remaining_to_return ?? false;
      this.edited.supervisor_id = row.supervisor_id ?? null;
      this.edited.supervisor_name = row.supervisor_name ?? null;
      this.edited.supervisor = row.supervisor ?? null;

    },
    openEditModal(row, editAction = 'all') {

      this.editAction = editAction;
      this.showSupervisorChange = false;
      this.setEdited(row);
      this.isEditLoading = true;

      this.loadSale(row.id)
        .then((sale) => {
          this.setEdited({
            ...row,
            ...sale,
            // Keep list-only flags that show() may not include.
            has_remaining_to_ship: row.has_remaining_to_ship,
            has_any_shipped: row.has_any_shipped,
            has_returns: row.has_returns,
            has_any_returned: row.has_any_returned,
            has_remaining_to_return: row.has_remaining_to_return,
          });
          this.unitChanged();
          this.loadBlockDates(null, true);
        })
        .catch(() => {
          toastFailure('Could not load order details');
        })
        .finally(() => {
          this.isEditLoading = false;
        });
    },
    shouldLockPaymentAmount(booking) {
      if (this.acceptPartialPayments) {
        return false
      }
      if (this.remainingToPay > 0) {
        return true
      }
      const paymentStatusId = booking && booking.payment_status !== undefined && booking.payment_status !== null
        ? Number(booking.payment_status)
        : this.orderPaymentStatusId
      return paymentStatusId === 0
    },
    applyPaymentDefaultsFromHistory(payments = []) {
      const list = Array.isArray(payments) ? payments : []
      this.payment.type = this.paymentTypes[list.length > 0 ? 1 : 0]
      this.payment.method = list.length > 0 ? list[0].method : DEFAULT_PAYMENT_METHOD
    },
    openAddPaymentModal(row) {

      this.payment.saleId = row.id;
      this.payment.amount = 0;
      this.payment.type = this.paymentTypes[Number(row.payment_status) > 0 ? 1 : 0];
      this.payment.method = DEFAULT_PAYMENT_METHOD;
      this.setEdited(row);
      this.updatePaymentDueFromSale(row, []);

      axios.get(`api/payment-history/${row.id}`)
        .then((response) => {
          const payments = response.data.data || []
          this.applyPaymentDefaultsFromHistory(payments)
          this.updatePaymentDueFromSale(row, payments)
          if (this.shouldLockPaymentAmount(row)) {
            this.payment.amount = Math.max(0, this.remainingToPay);
          }
        })
        .catch(() => {
          toastFailure('Could not load payment history')
        })
    },
    async deleteItem(id) {
      if (await this.confirmationMessage('Are you sure you want to delete this booking?')) {
        this.deleteSale(id)
          .then(() => {
            toastSuccess("Booking has been deleted.");
          })
          .catch(() => {
            toastFailure("Can not delete the booking.");
          });
      }
    },
    async editSubmit(event) {

      if (event) {
        event.stopPropagation();
      }

      if (this.isStaff && this.showStaffNewStatusHint && !this.hasStaffChangedOrderStatus) {
        toastFailure('Please change the order status before saving.')
        return
      }

      if (this.isOrderEditLocked && this.editAction !== 'status') {
        return
      }

      if (this.isOrderEditLocked && this.editAction === 'status') {
        if (this.hasDeletedOrderProducts) {
          toastFailure('Order status cannot be changed while the order includes deleted products.')
          return
        }

        if (this.showStaffNewStatusHint && (this.edited.status === null || this.edited.status === undefined)) {
          toastFailure('Please select a status.')
          return
        }

        if (!(await this.confirmStatusChangeIfNeeded())) {
          return
        }

        this.isEditLoading = true
        this.updateSale({
          id: this.edited.id,
          status: this.edited.status,
          status_only: true,
          ...(this.canChangeOrderSupervisor ? { supervisor_id: this.edited.supervisor_id } : {}),
        })
          .then((response) => {
            let emailsError = false
            if (response.emails_errors) {
              emailsError = {
                message: 'Your changes have been updated, but we couldn\'t send the emails because of technical error.',
              }
            }
            if (emailsError) {
              this.showError(emailsError, 1)
            } else {
              toastSuccess('Order status has been updated.')
            }
            this.edited.statusBeforeChange = this.edited.status
            this.$root.$emit('bv::hide::modal', 'edit-modal')
          })
          .catch((error) => {
            this.showError(error.response?.data || error)
          })
          .finally(() => {
            this.isEditLoading = false
          })
        return
      }

      if (this.editAction === 'status' && this.showStaffNewStatusHint && (this.edited.status === null || this.edited.status === undefined)) {
        toastFailure('Please select a status.')
        return
      }

      let productsValidation = false;

      const isFormCorrect = await this.vSale.$validate();

      if (!isFormCorrect) {

        console.log('Validation failed');
        console.log('Validation errors:', this.vSale.$errors);
        return;
      }

      if (companyPurpose !== 'rent') {
        const hasProducts = (this.edited.products || []).some((product) => product.items_quantity > 0)
        const hasServices = (this.edited.services || []).some((service) => service.items_quantity > 0)
        productsValidation = hasProducts || hasServices
      } else {
        productsValidation = true;
      }

      if (!productsValidation) {
        toastFailure('Please add a product or service to the order');
        return;
      }

      if (this.hasDeletedOrderProducts && Number(this.edited.status) !== Number(this.edited.statusBeforeChange)) {
        toastFailure('Order status cannot be changed while the order includes deleted products.')
        return
      }

      if (!(await this.confirmStatusChangeIfNeeded())) {
        return
      }

      this.isEditLoading = true;

      if (this.edited.cancelled && !(await this.confirmationMessage('Are you sure you want to cancel this booking?'))) {
        this.isEditLoading = false;
        return false;
      }

      let sendSurvey = false;
      if (!this.edited.cancelled && this.edited.payment_status.id === 6) {
        sendSurvey = await this.confirmationMessage('To send a review request to this customer click YES below. Otherwise, just click NO to complete charter.');
        this.$root.$emit('bv::hide::modal', 'edit-modal');
      }

      this.updateSale({
        id: this.edited.id,
        date: (this.edited.cancelled === true && this.edited.date === null) ? this.edited.beforeDate : this.edited.date,
        time: this.edited.time ? this.edited.time.value : null,
        package_name: this.edited.packageName,
        payment_status: this.edited.payment_status.id,
        status: this.edited.status ?? this.edited.statusBeforeChange,
        cancelled: this.edited.cancelled,
        details: this.edited.details,
        subtotal: this.edited.subtotal,
        shipping: this.edited.shipping,
        description: this.edited.description,
        customer_note: this.edited.customer_note,
        send_survey: sendSurvey,
        package_id: this.edited.packageName !== null ? this.thisUnitPackageNameId[this.edited.packageName] : null,
        unit_id: this.edited.unitObject !== null ? this.edited.unitObject.id : null,
        products: this.edited.products,
        services: this.edited.services,
        discount: this.edited.discount,
        time_interval: this.edited.time_interval,
        repeat: this.edited.repeat !== null ? (this.edited.repeat_details == '' ? this.edited.repeat : this.edited.repeat + '=' + this.edited.repeat_details) : null,
        pickup: this.edited.pickup,
        delivery: this.edited.delivery,
        ...(this.canChangeOrderSupervisor ? { supervisor_id: this.edited.supervisor_id } : {}),
        customer_address_country_id: this.edited.address != null ? (this.edited.address.country_id || window.companyCountryId) : null,
        customer_address_region_id: this.edited.address != null ? this.edited.address.region_id : null,
        customer_address_city: this.edited.address != null ? this.edited.address.city : null,
        customer_address_zip_code: this.edited.address != null ? this.edited.address.zip_code : null,
        customer_address_line_1: this.edited.address != null ? this.edited.address.address_line_1 : null,
        customer_address_line_2: this.edited.address != null ? this.edited.address.address_line_2 : null,
        customer_address_name: this.edited.address != null ? this.edited.address.name : null,
        customer_address_phone: this.edited.address != null ? this.edited.address.phone : null,
        customer_address_description: this.edited.address != null ? this.edited.address.description : null
      })
        .then((response) => {

          let emailsError = false;

          if( response.emails_errors ){
            emailsError = {
              message:'Your changes have been updated, but we couldn\'t send the emails because of technical error.'
            };
          }

          if (this.edited.cancelled) {
            if( emailsError ){
              this.showError(emailsError, 1);
            }
            else{
              toastSuccess("This booking has been canceled.");
            }
          }
          else {
            if( emailsError ){
              this.showError(emailsError, 1);
            }
            else{
              toastSuccess("Your changes have been updated.");
            }
          }

          this.$root.$emit("bv::hide::modal", "edit-modal");
        })
        .catch((error) => {
          this.showError(error.response.data);
        })
        .finally(() => {
          this.isEditLoading = false;
        });
    },
    addPaymentSubmit() {
      if (this.isOrderCancelled || this.isOrderFullyPaid) {
        toastFailure('Payments cannot be added to cancelled or fully paid orders.')
        return
      }

      if (!this.canSubmitAddPayment) {
        toastFailure('Payment amount must be greater than 0.')
        return
      }

      if (this.lockPaymentAmountToDue) {
        const due = this.paymentAmountDue
        const amount = parseFloat(this.payment.amount) || 0
        if (amount < due - 0.001) {
          toastFailure(`Payment amount cannot be less than the amount due (${this.$formatCurrency(due)}).`)
          return
        }
        this.payment.amount = due
      }

      this.isAddPaymentLoading = true;


        this.addSalePayment({
          sale_id: this.payment.saleId,
          amount: this.payment.amount,
          status: this.payment.type.value,
          method: this.payment.method,
        }).then((response) => {
          toastSuccess("Payment successfully added");

          if (response.sale_payment_status !== undefined) {
            this.edited.payment_status = this.paymentStatuses[response.sale_payment_status];
          }

          this.$root.$emit("bv::hide::modal", "add-payment-modal");
          this.$root.$emit("bv::hide::modal", "payment-history-modal");
        }).catch((error) => {
          this.showError(error.response?.data || error);
        }).finally(() => {
          this.isAddPaymentLoading = false;
        });
    },
    dateDisabled(date) {
      return checkReservedDate(date, this.blockDates, this.edited, this.units, this.thisUnitPackageNameId);
    },
    onSalesPageChange(params) {
      this.salesTablePage = params.currentPage
      this.reloadOrdersTable()
    },
    onTableSort(params) {
      const sortRule = Array.isArray(params) ? params[0] : params

      if (!sortRule?.field || !sortRule?.type) {
        this.tableSort = { field: 'created_at', type: 'desc' }
      } else {
        this.tableSort = {
          field: sortRule.field,
          type: sortRule.type,
        }
      }

      this.salesTablePage = 1
      this.reloadOrdersTable()
    },
    compareBookingRows(a, b, field, type) {
      const direction = type === 'desc' ? -1 : 1
      let left = a?.[field]
      let right = b?.[field]

      if (field === 'total') {
        left = this.$resolveSaleTotal(a)
        right = this.$resolveSaleTotal(b)
      } else if (field === 'unit') {
        left = this.bookingUnitSortValue(a)
        right = this.bookingUnitSortValue(b)
      } else if (field === 'pickup') {
        left = a?.pickup ? 1 : 0
        right = b?.pickup ? 1 : 0
      }

      if (left == null && right == null) {
        return 0
      }
      if (left == null) {
        return direction
      }
      if (right == null) {
        return -direction
      }

      if (field === 'date' || field === 'created_at') {
        const leftTime = new Date(left).getTime()
        const rightTime = new Date(right).getTime()
        return (leftTime - rightTime) * direction
      }

      const leftNumber = Number(left)
      const rightNumber = Number(right)
      if (!Number.isNaN(leftNumber) && !Number.isNaN(rightNumber)) {
        return (leftNumber - rightNumber) * direction
      }

      return String(left).localeCompare(String(right), undefined, {
        numeric: true,
        sensitivity: 'base',
      }) * direction
    },
    bookingUnitSortValue(row) {
      const products = row?.products || []
      if (products.length > 0) {
        return products.map((product) => product.name).join(', ')
      }

      return row?.unit || ''
    },
    onColumnFilter(params) {
      const columnFilters = params.columnFilters || {}

      if (Object.prototype.hasOwnProperty.call(columnFilters, 'status')) {
        const value = columnFilters.status
        this.filterStatusId = value !== '' && value !== null && value !== undefined
          ? String(value)
          : ''
      }

      if (Object.prototype.hasOwnProperty.call(columnFilters, 'payment_status')) {
        const value = columnFilters.payment_status
        this.filterPaymentStatusId = value !== '' && value !== null && value !== undefined
          ? String(value)
          : ''
      }

      if (Object.prototype.hasOwnProperty.call(columnFilters, 'unit')) {
        this.filterUnitKeyword = columnFilters.unit || ''
      }

      if (Object.prototype.hasOwnProperty.call(columnFilters, 'date')) {
        this.filterDeliveryDate = columnFilters.date || null
      }

      if (Object.prototype.hasOwnProperty.call(columnFilters, 'pickup')) {
        this.filterDeliveryDate = columnFilters.pickup || null
      }

      this.salesTablePage = 1
      this.changeUrl(columnFilters)
    },
    changeUrl(values) {
      const searchParams = new URLSearchParams(window.location.search);

      /* eslint-disable-next-line */
      for (const [key, value] of Object.entries(values)) {
        searchParams.set(key, value);
      }

      window.history.pushState(
        {},
        null,
        `${this.$route.path}?${searchParams.toString()}`
      );
    },
    disableSelectedDate(){
      this.edited.date = null;
      this.datepickerPlaceholder = 'Please select a new date';
    },
    loadBlockDates(event, modalOpened = false) {

      if (companyPurpose !== 'rent') {
        return;
      }

      if (this.edited.packageName) {
        const editedDate = this.edited.date;
        //this.edited.date = null;
        this.datepickerPlaceholder = "Loading...";
        this.isEditLoading = true;
        this.isDatepickerDisabled = true;
        axios
          .get(
            `api/sales/block-dates/${this.edited.unitObject.id}/${this.edited.packageName}`
          )
          .then((response) => {

            this.datepickerPlaceholder = 'Please choose the date';
            const blockDates = {};

            for (const date of response.data) {

              if( this.edited.date == date.date && this.edited.date != this.edited.beforeDate ){
                this.disableSelectedDate();
              }

              if (!blockDates[date.date]) {
                blockDates[date.date] = new Array();
              }

              blockDates[date.date].push(date);
            }

            this.blockDates = blockDates;
            this.isEditLoading = false;
            this.isDatepickerDisabled = false;

            if( this.dateDisabled(this.edited.date)  ){
              this.disableSelectedDate();
            }

            if (modalOpened || !Object.keys(this.blockDates).includes(editedDate)) {
              this.edited.date = editedDate;
            }

          })
          .catch(() => {
            toastFailure("Can not get blocked dates.");
          });
      }
    },
    isSelectable(time) {
      if (this.blockDates[this.edited.date]) {
        /* eslint-disable-next-line */
        return this.blockDates[this.edited.date].includes(time) ? false : true;
      }
      return true;
    },
    timesDisabled(option) {
      return option.selectable;
    },
    clickRow(params) {

      if (params.event.target.classList.contains("payment-status")) {

        this.payment.saleId = params.row.id;
        this.payment.amount = 0;
        this.payment.type = this.paymentTypes[Number(params.row.payment_status) > 0 ? 1 : 0];
        this.payment.method = DEFAULT_PAYMENT_METHOD;
        this.setEdited(params.row);

        this.getPaymentHistory(params.row);
        return;
      }

      if (params.event.target.classList.contains("customer-name")) {
        this.fieldCustomerDetails(params.row);
      }
    },
    fieldPaymentHistory(booking) {

      this.payment.saleId = booking.id;
      this.payment.amount = 0;
      this.payment.type = this.paymentTypes[Number(booking.payment_status) > 0 ? 1 : 0];
      this.payment.method = DEFAULT_PAYMENT_METHOD;
      this.setEdited(booking);

      this.getPaymentHistory(booking)
    },
    openManagePaymentsFromEdit() {
      const booking = this.salesWithPayments.find((item) => item.id === this.edited.id)
      if (!booking) {
        return
      }

      this.$bvModal.hide('edit-modal')
      this.$nextTick(() => {
        this.fieldPaymentHistory(booking)
      })
    },
    updatePaymentDueFromSale(booking, paymentsHistory = null) {
      this.totalPrice = this.$resolveSaleTotal(booking);

      let alreadyPaid = 0;
      let refunded = 0;

      if (paymentsHistory) {
        paymentsHistory.forEach((payment) => {
          alreadyPaid = this.$moneyRound(alreadyPaid + parseFloat(payment.paid));
          refunded = this.$moneyRound(refunded + parseFloat(payment.amount_refunded));
        });
      }

      this.alreadyPaid = alreadyPaid;
      this.refundedAmount = refunded;
      //const netPaid = this.$moneyRound(alreadyPaid - refunded);
      const netPaid = this.$moneyRound(alreadyPaid);
      this.remainingToPay = this.$moneyRemaining(this.totalPrice, netPaid);
    },
    getPaymentHistory(booking) {

      let saleId = booking.id;
      this.setEdited(booking);
      this.$root.$emit('bv::show::modal', 'payment-history-modal');
      this.isPaymentHistoryLoading = true;
      this.bookingLogs = booking.logs;
      axios.get(
          `api/payment-history/${saleId}`
        )
        .then(response => {
          this.paymentsHistory = response.data.data;
          this.applyPaymentDefaultsFromHistory(this.paymentsHistory);
          this.updatePaymentDueFromSale(booking, this.paymentsHistory);

          this.paymentsHistory.forEach((payment) => {
            if (this.refundAmountShowHide[payment.id] != undefined) {
              this.refundAmountShowHide[payment.id]['visible'] = 'hide';
            }
          });

          this.subTotal = booking.subtotal;
          this.paymentStatus = booking.status;
          if (this.shouldLockPaymentAmount(booking)) {
            this.payment.amount = Math.max(0, this.remainingToPay);
          }
          this.isPaymentHistoryLoading = false;
          this.edited.unitObject = this.resolveUnitObject(booking);
        })
        .catch(() => {
          this.isPaymentHistoryLoading = false;
          toastFailure("Something went wrong while loading the payments.")
        });
    },
    confirmationMessage(message) {
      return this.$bvModal.msgBoxConfirm(message, {
        title: 'Please Confirm',
        okVariant: 'primary',
        okTitle: 'Yes',
        cancelTitle: 'No',
      });
    },
    async confirmStatusChangeIfNeeded() {
      if (this.companyPurpose === 'rent') {
        return true
      }
      const nextStatus = this.edited.status ?? this.edited.statusBeforeChange
      if (Number(nextStatus) === Number(this.edited.statusBeforeChange)) {
        return true
      }
      const fromLabel = this.getStatusDisplay(this.edited.statusBeforeChange, this.edited).name
      const toLabel = this.getStatusDisplay(nextStatus, this.edited).name
      let message = `Change order status from "${fromLabel}" to "${toLabel}"?`

      const isClaimingAsStaff = this.isStaff
        && Number(this.edited.statusBeforeChange) === 0
        && (this.edited.supervisor_id === null || this.edited.supervisor_id === undefined || this.edited.supervisor_id === '')

      if (isClaimingAsStaff) {
        message += ' You will become the supervisor of this order, and only you and the company owner will have control over it.'
      }

      return this.confirmationMessage(message)
    },
    dateChanged() {
      this.edited.time = null;
    },
    fieldCustomerDetails(row) {
      this.customer.name = row.customer_name;
      this.customer.email = row.customer_email;
      this.customer.phone = row.phone;
      this.customer.stripe_id = row.customer_stripe_id;
      this.customer.description = row.user ? row.user.description : '';

      this.$root.$emit('bv::show::modal', 'customer-modal');
    },

    calculatePrice(sale) {
      return this.$resolveSaleTotal(sale);
    }

  },
};
</script>

<style scoped>
.order-supervisor-thumb {
  width: 48px;
  height: 48px;
  object-fit: cover;
  border: 1px solid #ddd;
  border-radius: 50%;
  flex-shrink: 0;
}

.order-supervisor-thumb--table {
  width: 28px;
  height: 28px;
}

.order-supervisor-header {
  gap: 0.5rem;
  flex-wrap: wrap;
}

.order-supervisor-change {
  min-width: 220px;
  max-width: 280px;
  flex: 1 1 220px;
}

.order-supervisor-change :deep(.image-text-select-thumb) {
  border-radius: 50%;
}

.deleted-product-name {
  text-decoration: line-through;
  color: #6e6b7b;
}

.order-line-qty {
  font-size: 0.85em;
  font-weight: 600;
}

.order-lines-font-1 {
  font-size: 15px;
}

.order-lines-font-2 {
  font-size: 13px;
}

.order-lines-font-3 {
  font-size: 11.5px;
}

.order-lines-font-4 {
  font-size: 10px;
}

.order-lines-view-all {
  padding: 0.1rem 0.35rem !important;
  font-size: 10px !important;
  line-height: 1.2 !important;
}

.payment-view-btn {
  line-height: 1;
  color: #5e5873;
}

.payment-refund-detail__row {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
  padding: 0.55rem 0;
  border-bottom: 1px solid #dae1e7;
}

.payment-refund-detail__row:last-of-type {
  border-bottom: 0;
}

.payment-refund-detail__label {
  font-size: 0.75rem;
  color: #6e6b7b;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.payment-refund-detail__value {
  font-size: 0.95rem;
  color: #5e5873;
  word-break: break-word;
}

@media (max-width: 991.98px) {
  .payments-history-table--compact :deep(.vgt-checkbox-col) {
    display: none !important;
  }
}
</style>